import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Landmark,
  Mountain,
  Laugh,
  Theater,
  Wand2,
  Check,
  Clapperboard,
  Youtube,
  Zap,
  Instagram,
  Loader2,
  ChevronDown,
  Layers,
  Play,
  Pause,
  Volume2,
  Download,
  Edit3,
  Film,
  RotateCcw,
  Palette,
  X,
  Search,
  Lock,
  Mic,
  Maximize2,
  LayoutDashboard,
  ExternalLink,
  AlertCircle,
  RefreshCw,
  Info,
  Upload,
  Square,
  FileAudio,
  FileUp,
  Music2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { useInsufficientCredits, handleInsufficientCreditsError } from '@/contexts/InsufficientCreditsContext';
import { removeNarrationCreateDraft } from '@/lib/narrationCreateDraft';
import { buildNarrationCreatePayload } from '@/lib/narrationCreatePayload';
import { STORY_FLOWS, storyFlowNavigationState } from '@/data/storyFlows';
import { uploadIntakeMediaAndCreateStoryTask } from '@/lib/intakeFileUpload';
import { leftTopArrowGradient, rightBottomArrowGradient, twinneeLogo } from '@/assets/svgs/gradientIcons';
import { Textarea } from '@/components/ui/textarea';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';
import { pickOption, readAgentHandoff } from '@/lib/agentHandoff';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/* ─────────────────────────────────────────
   Figma Exact Tick & Radio Components
───────────────────────────────────────── */
const FigmaTickBadge = ({ className = "w-4 h-4" }) => (
  <div className={`${className} rounded-full bg-[#2DD4BF] flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(45,212,191,0.5)]`}>
    <svg
      className="w-2.5 h-2.5 text-[#06080D]"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 7.5L5.5 10L11 4"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const FigmaRadioTick = ({ selected = false, className = "w-4 h-4" }) => (
  <div
    className={`${className} rounded-full border transition-all flex items-center justify-center shrink-0 ${selected
      ? "border-[#2DD4BF] bg-[#2DD4BF] shadow-[0_0_8px_rgba(45,212,191,0.4)]"
      : "border-white/25 bg-transparent hover:border-white/40"
      }`}
  >
    {selected && (
      <svg
        className="w-2.5 h-2.5 text-[#06080D]"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 7.5L5.5 10L11 4"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </div>
);

const FigmaPurpleTickBadge = () => (
  <div className="w-4 h-4 rounded-full bg-[#8B5CF6] flex items-center justify-center shadow-[0_0_8px_rgba(139,92,246,0.5)]">
    <svg
      className="w-2.5 h-2.5 text-white"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 7.5L5.5 10L11 4"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/* ─────────────────────────────────────────
   Genre Options (Step 1 - Figma Exact Icons)
───────────────────────────────────────── */
const GENRE_OPTIONS = [
  {
    id: 'bedtime',
    title: 'Bedtime story',
    subtitle: 'Gentle kids narration',
    icon: BookOpen,
    defaultTone: 'gentle & calming',
    defaultPrompt: 'A gentle and magical bedtime story for children, soothing tone, heartwarming lesson at the end.',
  },
  {
    id: 'mythology',
    title: 'Mythology',
    subtitle: 'Gods, epics, folklore',
    icon: Landmark,
    defaultTone: 'epic & ancient',
    defaultPrompt: 'An epic tale of ancient gods, legendary heroes, and mythical creatures battling for cosmic balance.',
  },
  {
    id: 'adventure',
    title: 'Adventure',
    subtitle: 'Quest and action',
    icon: Mountain,
    defaultTone: 'thrilling & fast-paced',
    defaultPrompt: 'A high-stakes quest through uncharted lands filled with hidden traps, ancient secrets, and daring escapes.',
  },
  {
    id: 'comedy',
    title: 'Comedy',
    subtitle: 'Light and funny',
    icon: Theater,
    defaultTone: 'witty & humorous',
    defaultPrompt: 'A hilarious comedy with witty banter, silly misunderstandings, and cheerful character moments.',
  },
];

/* ─────────────────────────────────────────
   Voice Providers (Step 3) - ElevenLabs, Murf, HeyGen
───────────────────────────────────────── */
const VOICE_PROVIDERS = [
  { id: 'elevenlabs', name: 'ElevenLabs' },
  { id: 'murf', name: 'Murf' },
  { id: 'heygen', name: 'HeyGen' },
];

/* ─────────────────────────────────────────
   Visual Styles (Step 4)
───────────────────────────────────────── */
const VISUAL_STYLES = [
  {
    id: 'pixar',
    name: 'Pixar / Toy Story',
    badge: 'WARM 3D',
    image: '/flowImages/1.png',
    promptModifier: 'warm 3D animated style, vibrant lighting, Pixar inspired character render, rich textures',
  },
  {
    id: 'anime',
    name: 'Anime',
    badge: 'CEL SHADE',
    image: '/flowImages/2.png',
    promptModifier: 'high quality modern anime style, clean line art, vibrant cel shading, cinematic anime aesthetic',
  },
  {
    id: 'ghibli',
    name: 'Studio Ghibli',
    badge: 'SOFT PAINT',
    image: '/flowImages/3.png',
    promptModifier: 'hand-painted Studio Ghibli style, lush watercolor scenery, whimsical atmosphere, soft natural lighting',
  },
  {
    id: 'hyper_real',
    name: 'Hyper-Real',
    badge: 'PHOTOREAL',
    image: '/flowImages/4.png',
    promptModifier: 'photorealistic 8k cinematic shot, 35mm lens, atmospheric depth of field, award-winning cinematography',
  },
  {
    id: 'cyber_synth',
    name: 'Cyber-Synth',
    badge: 'SYNTHWAVE',
    image: '/LandingPageAssets/content-creator.png',
    promptModifier: 'neon cyberpunk synthwave aesthetic, glowing purple and teal rim lights, futuristic city vibes',
  },
  {
    id: 'noir_sketch',
    name: 'Noir Sketch',
    badge: 'MONOCHROME',
    image: '/LandingPageAssets/movie-creator.png',
    promptModifier: 'dramatic charcoal noir sketch, high contrast black and white, dynamic rough pencil shading',
  },
];

/* ─────────────────────────────────────────
   Languages & Presets
───────────────────────────────────────── */
/** Create-agent visual style ids → closest style card on this page. */
const AGENT_STYLE_TO_NARRATION = {
  pixar_cinematic: 'pixar',
  claymation: 'pixar',
  comic_book: 'anime',
  minimalist_flat: 'pixar',
  anime: 'anime',
  studio_ghibli: 'ghibli',
  watercolor_storybook: 'ghibli',
  photoreal_cinematic: 'hyper_real',
  hyperrealistic: 'hyper_real',
  cyberpunk_neon: 'cyber_synth',
  noir_film: 'noir_sketch',
};

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'ja', label: 'Japanese' },
];

const LENGTH_OPTIONS = [
  { id: 'short', label: 'Short', duration: '1–2 min', scenes: 4 },
  { id: 'medium', label: 'Medium', duration: '2–5 min', scenes: 6 },
  { id: 'long', label: 'Long', duration: '5–10 min', scenes: 8 },
];

const CREATING_FOR_OPTIONS = [
  {
    id: 'YouTube, landscape',
    ratio: '16:9',
    title: 'YouTube, landscape',
    subtitle: '16:9 widescreen',
  },
  {
    id: 'Shorts, Reels, TikTok',
    ratio: '9:16',
    title: 'Shorts, Reels, TikTok',
    subtitle: '9:16 vertical video',
  },
  {
    id: 'Instagram feed',
    ratio: '1:1',
    title: 'Instagram feed',
    subtitle: '1:1 square post',
  },
  {
    id: 'TV, presentations',
    ratio: '16:9',
    title: 'TV, presentations',
    subtitle: '16:9 display format',
  },
  {
    id: 'Tall portrait frames',
    ratio: '9:16',
    title: 'Tall portrait frames',
    subtitle: '9:16 portrait canvas',
  },
  {
    id: 'Photo-style portrait',
    ratio: '4:5',
    title: 'Photo-style portrait',
    subtitle: '4:5 / 3:4 portrait',
  },
];

const IMAGE_PROVIDERS = [
  { id: 'gemini_nano', label: 'Gemini Nano Banana' },
  { id: 'flux_pro', label: 'Flux Pro Ultra' },
  { id: 'runway_gen3', label: 'Runway Gen-3 Alpha' },
  { id: 'midjourney_v6', label: 'Midjourney Photoreal' },
  { id: 'inhouse', label: 'Inhouse' },
];

/* ─────────────────────────────────────────
   Roadmap Steps Definition
───────────────────────────────────────── */
const ROADMAP_STEPS = [
  {
    id: 1,
    title: 'Genre & Concept',
    shortDesc: 'Story genre & custom idea',
    icon: BookOpen,
  },
  {
    id: 2,
    title: 'Story Setup',
    shortDesc: 'Title, length & aspect ratio',
    icon: Layers,
  },
  {
    id: 3,
    title: 'Voice Configuration',
    shortDesc: 'AI voice & audio tuning',
    icon: Volume2,
  },
  {
    id: 4,
    title: 'Visual Style',
    shortDesc: 'Art direction & provider',
    icon: Palette,
  },
  {
    id: 5,
    title: 'Summary & Publish',
    shortDesc: 'Review summary & queue',
    icon: Film,
  },
  {
    id: 6,
    title: 'Video Queued',
    shortDesc: 'Publishing queue status',
    icon: TwinneeLogo,
  },
];

export const NarrationFlowPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openInsufficientCredits } = useInsufficientCredits();
  const agentHandoff = React.useMemo(() => readAgentHandoff(location.state), [location.state]);
  const [handoffReady, setHandoffReady] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [isScrolled, setIsScrolled] = useState(false);

  // Step 1
  const [selectedGenre, setSelectedGenre] = useState('');
  const [customGenrePrompt, setCustomGenrePrompt] = useState('');
  const [intakeMode, setIntakeMode] = useState('write');
  const [intakeText, setIntakeText] = useState('');
  const [intakeAudioUrl, setIntakeAudioUrl] = useState('');
  const [intakeAudioName, setIntakeAudioName] = useState('');
  const [intakeAudioDuration, setIntakeAudioDuration] = useState(0);
  const [intakeTranscript, setIntakeTranscript] = useState('');
  const [intakeFileMeta, setIntakeFileMeta] = useState(null);
  const [classification, setClassification] = useState(null);
  const [classifyBusy, setClassifyBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [genreUserEdited, setGenreUserEdited] = useState(false);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const classifyTimerRef = useRef(null);
  const intakeSnapshotRef = useRef({
    text: '',
    transcript: '',
    extractedText: '',
    audioUrl: '',
    audioName: '',
    audioDuration: 0,
    selectedGenre: '',
    genreDirty: false,
    fileMeta: null,
    intakeMode: 'write',
  });

  // Step 2
  const [title, setTitle] = useState('');
  const [shortIdea, setShortIdea] = useState('');
  const [language, setLanguage] = useState('');
  const [lengthPreset, setLengthPreset] = useState('');
  const [aspectRatio, setAspectRatio] = useState('');

  // Step 3
  const [selectedVoiceProvider, setSelectedVoiceProvider] = useState('elevenlabs');
  const [selectedVoiceLanguage, setSelectedVoiceLanguage] = useState('all');
  const [selectedVoiceGender, setSelectedVoiceGender] = useState('all');
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);
  const [voicePitch, setVoicePitch] = useState(0);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [samplePlayingTime, setSamplePlayingTime] = useState(0);
  const [playingVoiceCardId, setPlayingVoiceCardId] = useState(null);
  const [isVoicesModalOpen, setIsVoicesModalOpen] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalProviderFilter, setModalProviderFilter] = useState('all');
  const [modalLanguageFilter, setModalLanguageFilter] = useState('all');
  const [modalGenderFilter, setModalGenderFilter] = useState('all');

  // Step 4
  const [selectedStyle, setSelectedStyle] = useState('');
  const [imageProvider, setImageProvider] = useState('');
  const [tone, setTone] = useState('');
  const [sceneCount, setSceneCount] = useState(6);
  const [generationBrief, setGenerationBrief] = useState('');

  // Step 5
  const [isPreviewVideoPlaying, setIsPreviewVideoPlaying] = useState(false);

  // Step 5 Publishing Destination Options
  const [selectedPlatforms, setSelectedPlatforms] = useState({
    youtube: false,
    youtube_shorts: false,
    instagram_reels: false,
  });

  // Step 6 Queued Process State
  const [queueStatus, setQueueStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [queueError, setQueueError] = useState(null);
  const [queuedTimestamp, setQueuedTimestamp] = useState(null);

  // Final Output
  const [finalVideoPlaying, setFinalVideoPlaying] = useState(false);

  // Pipeline State
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobMessage, setJobMessage] = useState('Queued');
  const pollRef = useRef(null);

  // Live API Key Voices State
  const [apiVoices, setApiVoices] = useState({
    elevenlabs: [
      {
        id: 'marcus',
        name: 'Marcus',
        provider: 'elevenlabs',
        gender: 'male',
        language: 'English (US)',
        tagline: 'Deep • Narrative',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80',
      },
      {
        id: 'sophia',
        name: 'Sophia',
        provider: 'elevenlabs',
        gender: 'female',
        language: 'English (US)',
        tagline: 'Energetic • Clear',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
      },
      {
        id: 'elias',
        name: 'Elias',
        provider: 'elevenlabs',
        gender: 'male',
        language: 'English (US)',
        tagline: 'Gravelly • Serious',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&auto=format&fit=crop&q=80',
      },
      {
        id: 'aria',
        name: 'Aria',
        provider: 'elevenlabs',
        gender: 'female',
        language: 'English (US)',
        tagline: 'Soft • Ethereal',
        avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&auto=format&fit=crop&q=80',
      },
    ],
    murf: [],
    heygen: [],
  });
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [providerErrors, setProviderErrors] = useState({});
  const activeAudioElemRef = useRef(null);
  const [recommendBusy, setRecommendBusy] = useState(false);
  const [recommendRationale, setRecommendRationale] = useState('');
  const dirtyFieldsRef = useRef(new Set());

  useEffect(() => {
    intakeSnapshotRef.current = {
      text: intakeText,
      transcript: intakeTranscript,
      extractedText: '',
      audioUrl: intakeAudioUrl,
      audioName: intakeAudioName,
      audioDuration: intakeAudioDuration,
      selectedGenre,
      genreDirty: genreUserEdited,
      fileMeta: intakeFileMeta,
      intakeMode,
    };
  }, [
    intakeText,
    intakeTranscript,
    intakeAudioUrl,
    intakeAudioName,
    intakeAudioDuration,
    selectedGenre,
    genreUserEdited,
    intakeFileMeta,
    intakeMode,
  ]);

  const dashboardPrefillAppliedRef = useRef(false);
  useEffect(() => {
    if (dashboardPrefillAppliedRef.current) return;
    const prefill = location.state?.prefill;
    const quickIntake = location.state?.quickIntake;
    const recommended = location.state?.recommendedParams || quickIntake?.recommendedParams;
    if (!prefill && !quickIntake && !recommended) return;
    dashboardPrefillAppliedRef.current = true;

    const idea = (
      prefill?.shortIdea ||
      recommended?.topic ||
      quickIntake?.text ||
      quickIntake?.transcript ||
      ''
    ).trim();
    if (idea) {
      setIntakeText(idea);
      setCustomGenrePrompt(idea.slice(0, 300));
      setShortIdea(idea.slice(0, 500));
      setGenerationBrief(prefill?.generationBrief || idea.slice(0, 2000));
    }
    if (prefill?.title || recommended?.title) setTitle(prefill?.title || recommended.title);
    const genreId = prefill?.genreId || recommended?.genre_id || quickIntake?.genreId;
    if (genreId) setSelectedGenre(genreId);
    if (prefill?.tone || recommended?.tone) {
      setTone(prefill?.tone || recommended.tone);
    }
    if (prefill?.visualStyle || recommended?.visual_style_ui || recommended?.visual_style) {
      setSelectedStyle(prefill?.visualStyle || recommended.visual_style_ui || recommended.visual_style);
    }
    if (prefill?.ttsProvider || recommended?.tts_provider) {
      setSelectedVoiceProvider(prefill?.ttsProvider || recommended.tts_provider);
    }
    if (prefill?.imageProvider || recommended?.image_provider) {
      setImageProvider(prefill?.imageProvider || recommended.image_provider);
    }
    if (prefill?.segmentCount || recommended?.segment_count) {
      setSceneCount(Number(prefill?.segmentCount || recommended.segment_count) || 6);
    }
    if (quickIntake?.audioUrl) {
      setIntakeAudioUrl(quickIntake.audioUrl);
      setIntakeAudioName(quickIntake.audioFilename || 'audio');
    }
    if (quickIntake?.transcript) setIntakeTranscript(quickIntake.transcript);
    if (quickIntake?.classification) setClassification(quickIntake.classification);
    if (quickIntake?.fileUrl || quickIntake?.storyId) {
      setIntakeFileMeta({
        filename: quickIntake.filename || 'upload',
        url: quickIntake.fileUrl,
        storyId: quickIntake.storyId,
        kind: quickIntake.fileKind || 'file',
      });
    }
    if (readAgentHandoff(location.state)) {
      const style = prefill?.visualStyle || '';
      const mappedStyle = pickOption(style, VISUAL_STYLES.map((s) => s.id), AGENT_STYLE_TO_NARRATION[style]);
      if (mappedStyle) setSelectedStyle(mappedStyle);
      const lang = pickOption(prefill?.language, LANGUAGES.map((l) => l.code), '');
      if (lang) setLanguage(lang);
      const ratioOpt = CREATING_FOR_OPTIONS.find((o) => o.ratio === prefill?.aspectRatio);
      if (ratioOpt) setAspectRatio(ratioOpt.id);
      // The agent's answers win over the page's auto-recommend pass.
      const answered = {
        tone: prefill?.tone,
        selectedStyle: mappedStyle,
        selectedVoiceProvider: prefill?.ttsProvider,
        imageProvider: prefill?.imageProvider,
        language: lang,
        sceneCount: prefill?.segmentCount,
        generationBrief: prefill?.generationBrief || idea,
        aspectRatio: ratioOpt?.id,
        selectedGenre: genreId,
      };
      Object.entries(answered).forEach(([key, v]) => {
        if (v) dirtyFieldsRef.current.add(key);
      });
      setHandoffReady(true);
    }
    if (idea || quickIntake?.audioUrl || quickIntake?.fileUrl) setCurrentStep(2);
  }, [location.state]);

  const markDirty = (key) => {
    dirtyFieldsRef.current.add(key);
  };

  const setIfClean = (key, setter, value) => {
    if (dirtyFieldsRef.current.has(key)) return;
    if (value === undefined || value === null || value === '') return;
    setter(value);
  };

  // Fetch voices directly from selected provider's API key
  const fetchVoicesForProvider = React.useCallback(async (provider) => {
    if (!provider) return;
    setLoadingVoices(true);
    setProviderErrors((prev) => ({ ...prev, [provider]: null }));
    try {
      const endpoint = `/tts/voices/${provider}`;
      const res = await api.get(endpoint);
      const rawVoices = res.data?.voices || [];

      if (rawVoices.length > 0) {
        const normalized = rawVoices.map((v, idx) => {
          const id = v.voice_id || v.id || `${provider}-${idx}`;
          const name = v.name || v.displayName || id;
          const gender = (
            v.gender ||
            v.labels?.gender ||
            (name.toLowerCase().includes('female') ? 'female' : 'male')
          ).toLowerCase();
          const lang =
            v.language ||
            v.locale ||
            v.labels?.accent ||
            v.labels?.language ||
            'English';
          const tagline =
            v.tagline ||
            v.description ||
            v.labels?.description ||
            v.labels?.['use case'] ||
            v.category ||
            `${provider.toUpperCase()} Studio Voice`;
          const previewUrl = v.preview_url || v.preview_audio || v.sampleUrl || null;

          return {
            id,
            name,
            provider,
            gender: gender === 'female' ? 'female' : 'male',
            language: lang,
            tagline,
            preview_url: previewUrl,
            avatar: `https://images.unsplash.com/photo-${gender === 'female'
              ? '1534528741775-53994a69daeb'
              : '1507003211169-0a1dd7228f2d'
              }?w=150&auto=format&fit=crop&q=80`,
          };
        });

        setApiVoices((prev) => ({
          ...prev,
          [provider]: normalized,
        }));
      }
    } catch (err) {
      console.warn(`Error fetching ${provider} voices:`, err);
      setProviderErrors((prev) => ({
        ...prev,
        [provider]: formatApiError(err, `Failed to load ${provider} voices`),
      }));
    } finally {
      setLoadingVoices(false);
    }
  }, []);

  // Fetch configured providers on mount
  useEffect(() => {
    VOICE_PROVIDERS.forEach((p) => {
      fetchVoicesForProvider(p.id);
    });
  }, [fetchVoicesForProvider]);

  const stopAllAudio = () => {
    if (activeAudioElemRef.current) {
      activeAudioElemRef.current.pause();
      activeAudioElemRef.current.currentTime = 0;
      activeAudioElemRef.current = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  const playVoiceSampleAudio = async (voice, speed = 1.0, pitch = 0) => {
    stopAllAudio();

    // 1. Direct High-Quality API Key preview audio playback
    if (voice.preview_url) {
      try {
        const audio = new Audio(voice.preview_url);
        activeAudioElemRef.current = audio;
        audio.playbackRate = Math.max(0.5, Math.min(2.0, speed));
        audio.onended = () => {
          setIsPlayingVoice(false);
          setPlayingVoiceCardId(null);
        };
        audio.onerror = () => {
          generateAndPlaySample(voice, speed);
        };
        await audio.play();
        return;
      } catch (err) {
        console.warn('Direct preview error, calling sample API:', err);
      }
    }

    // 2. Generate live audio via server API key
    await generateAndPlaySample(voice, speed);
  };

  const generateAndPlaySample = async (voice, speed = 1.0) => {
    try {
      const isHindi =
        voice.language?.toLowerCase().includes('hindi') || voice.language?.toLowerCase().includes('hi');
      const sampleText = isHindi
        ? `नमस्ते! मैं ${voice.name} हूँ। ट्विन टेल्स स्टूडियो में आपकी कहानी को जीवंत बनाना मेरा काम है।`
        : `Hello! I'm ${voice.name}. Welcome to Twin Tales studio. I will narrate your story with emotion and clarity.`;

      const response = await api.post(
        '/tts/voices/sample',
        {
          provider: voice.provider,
          voice_id: voice.id,
          text: sampleText,
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(blob);
      const audio = new Audio(blobUrl);
      activeAudioElemRef.current = audio;
      audio.playbackRate = Math.max(0.5, Math.min(2.0, speed));
      audio.onended = () => {
        setIsPlayingVoice(false);
        setPlayingVoiceCardId(null);
        URL.revokeObjectURL(blobUrl);
      };
      await audio.play();
    } catch (err) {
      console.warn('API Sample generation error, using speech synthesis:', err);
      fallbackSpeechSynthesis(voice, speed);
    }
  };

  const fallbackSpeechSynthesis = (voice, speed = 1.0) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setIsPlayingVoice(false);
      setPlayingVoiceCardId(null);
      return;
    }
    const isHindi = voice.language?.toLowerCase().includes('hindi');
    const sampleText = isHindi
      ? `नमस्ते! मैं ${voice.name} हूँ। ट्विन टेल्स में आपका स्वागत है।`
      : `Hello! I'm ${voice.name}. Welcome to Twin Tales studio.`;

    const utterance = new SpeechSynthesisUtterance(sampleText);
    utterance.rate = Math.max(0.7, Math.min(1.8, speed));
    utterance.lang = isHindi ? 'hi-IN' : 'en-US';
    utterance.onend = () => {
      setIsPlayingVoice(false);
      setPlayingVoiceCardId(null);
    };
    utterance.onerror = () => {
      setIsPlayingVoice(false);
      setPlayingVoiceCardId(null);
    };
    window.speechSynthesis.speak(utterance);
  };

  const handleTogglePlayVoice = (voice) => {
    if (playingVoiceCardId === voice.id && isPlayingVoice) {
      stopAllAudio();
      setIsPlayingVoice(false);
      setPlayingVoiceCardId(null);
    } else {
      setSelectedVoiceId(voice.id);
      setPlayingVoiceCardId(voice.id);
      setIsPlayingVoice(true);
      setSamplePlayingTime(0);
      playVoiceSampleAudio(voice, voiceSpeed, voicePitch);
    }
  };

  useEffect(() => {
    return () => stopAllAudio();
  }, []);

  useEffect(() => {
    if (dirtyFieldsRef.current.has('generationBrief')) return;
    const genreObj = GENRE_OPTIONS.find((g) => g.id === selectedGenre);
    if (genreObj && title.trim()) {
      setGenerationBrief(
        `A captivating ${genreObj.title.toLowerCase()} titled "${title}". ${shortIdea ? shortIdea + '. ' : ''
        }Keep it audience-friendly and visually clear scene to scene. Emphasize the emotional journey and character transformation.`
      );
    }
  }, [selectedGenre, title, shortIdea]);

  useEffect(() => {
    let interval;
    if (isPlayingVoice) {
      interval = setInterval(() => {
        setSamplePlayingTime((prev) => {
          if (prev >= 12) {
            setIsPlayingVoice(false);
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlayingVoice]);

  const handleLengthChange = (presetId) => {
    markDirty('lengthPreset');
    markDirty('sceneCount');
    setLengthPreset(presetId);
    const opt = LENGTH_OPTIONS.find((o) => o.id === presetId);
    if (opt) setSceneCount(opt.scenes);
  };

  const handleTogglePlatform = (key) => {
    markDirty('platforms');
    setSelectedPlatforms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allAvailableVoices = React.useMemo(() => {
    const el = apiVoices.elevenlabs || [];
    const mu = apiVoices.murf || [];
    const mm = apiVoices.minimax || [];
    return [...el, ...mu, ...mm];
  }, [apiVoices]);

  const selectedVoice =
    allAvailableVoices.find((v) => v.id === selectedVoiceId) || {
      id: '',
      name: 'No Voice Selected',
      provider: selectedVoiceProvider || '',
      gender: '',
      language: '',
      tagline: 'Please select a voice',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
    };

  const filteredVoices = allAvailableVoices.filter((v) => {
    if (v.provider !== selectedVoiceProvider) return false;
    if (selectedVoiceLanguage !== 'all' && !v.language?.toLowerCase().includes(selectedVoiceLanguage.toLowerCase())) return false;
    if (selectedVoiceGender !== 'all' && v.gender !== selectedVoiceGender) return false;
    return true;
  });

  const handleAutoExpand = () => {
    const genreObj = GENRE_OPTIONS.find((g) => g.id === selectedGenre);
    const styleObj = VISUAL_STYLES.find((s) => s.id === selectedStyle);
    const expanded = `Create a cinematic ${genreObj?.title || 'story'} titled "${title || 'XBFB'}". Visual aesthetics: ${styleObj?.promptModifier || 'cinematic warm render'
      }. Plot context: ${shortIdea || customGenrePrompt || 'A heartwarming tale of discovery and courage across vivid scenic landscapes'
      }. Voice: ${selectedVoice.name} (${selectedVoice.tagline}), Tone: ${tone}. Total scenes: ${sceneCount}. Seamless voice narration with emotional pacing and coherent character rendering.`;
    setGenerationBrief(expanded);
    markDirty('generationBrief');
    toast.success('Prompt brief expanded');
  };

  const applyRecommendations = (recs) => {
    if (!recs) return;
    if (recs.rationale) setRecommendRationale(recs.rationale);
    setIfClean('tone', setTone, recs.tone);
    setIfClean('selectedStyle', setSelectedStyle, recs.visual_style_ui || recs.visual_style);
    setIfClean('selectedVoiceProvider', setSelectedVoiceProvider, recs.tts_provider);
    setIfClean('imageProvider', setImageProvider, recs.image_provider);
    setIfClean('language', setLanguage, recs.language);
    if (!dirtyFieldsRef.current.has('sceneCount') && recs.segment_count) {
      setSceneCount(Number(recs.segment_count) || 6);
    }
    if (!dirtyFieldsRef.current.has('lengthPreset') && recs.length) {
      setLengthPreset(recs.length);
    }
    setIfClean('generationBrief', setGenerationBrief, recs.topic);
    if (!dirtyFieldsRef.current.has('aspectRatio') && recs.image_aspect_ratio) {
      const match = CREATING_FOR_OPTIONS.find((o) => o.ratio === recs.image_aspect_ratio);
      if (match) setAspectRatio(match.id);
    }
    if (!dirtyFieldsRef.current.has('platforms')) {
      setSelectedPlatforms({
        youtube: Boolean(recs.publish_youtube),
        youtube_shorts: Boolean(recs.publish_youtube_shorts),
        instagram_reels: Boolean(recs.publish_instagram_reels),
      });
    }
  };

  const runRecommend = async ({ toastOnSuccess = false } = {}) => {
    setRecommendBusy(true);
    try {
      const ratio =
        CREATING_FOR_OPTIONS.find((opt) => opt.id === aspectRatio)?.ratio || '16:9';
      const { data } = await api.post('/narration/recommend', {
        title: title.trim(),
        genre_id: selectedGenre,
        custom_genre: customGenrePrompt.trim(),
        idea: shortIdea.trim(),
        language: language || 'en',
        length: lengthPreset || 'medium',
        aspect_ratio: ratio,
        publish_targets: [
          ...(selectedPlatforms.youtube ? ['youtube'] : []),
          ...(selectedPlatforms.youtube_shorts ? ['yt_shorts'] : []),
          ...(selectedPlatforms.instagram_reels ? ['instagram_reels'] : []),
        ],
      });
      applyRecommendations(data?.recommendations);
      if (toastOnSuccess) {
        toast.success(data?.recommendations?.rationale || 'Settings auto-matched');
      }
      return data?.recommendations;
    } catch (err) {
      // Client fallback presets
    if (selectedGenre === 'bedtime') {
        setIfClean('selectedStyle', setSelectedStyle, 'pixar');
        setIfClean('selectedVoiceProvider', setSelectedVoiceProvider, 'murf');
        setIfClean('tone', setTone, 'gentle & heartwarming');
    } else if (selectedGenre === 'mythology') {
        setIfClean('selectedStyle', setSelectedStyle, 'hyper_real');
        setIfClean('selectedVoiceProvider', setSelectedVoiceProvider, 'elevenlabs');
        setIfClean('tone', setTone, 'grand & epic');
    } else if (selectedGenre === 'comedy') {
        setIfClean('selectedStyle', setSelectedStyle, 'anime');
        setIfClean('selectedVoiceProvider', setSelectedVoiceProvider, 'minimax');
        setIfClean('tone', setTone, 'humorous & fast-paced');
    } else {
        setIfClean('selectedStyle', setSelectedStyle, 'ghibli');
        setIfClean('selectedVoiceProvider', setSelectedVoiceProvider, 'elevenlabs');
        setIfClean('tone', setTone, 'inspiring & adventurous');
      }
      if (toastOnSuccess) toast.message('Used local genre presets');
      return null;
    } finally {
      setRecommendBusy(false);
    }
  };

  const handleReRecommend = () => {
    dirtyFieldsRef.current.delete('tone');
    dirtyFieldsRef.current.delete('selectedStyle');
    dirtyFieldsRef.current.delete('selectedVoiceProvider');
    dirtyFieldsRef.current.delete('imageProvider');
    dirtyFieldsRef.current.delete('sceneCount');
    dirtyFieldsRef.current.delete('generationBrief');
    dirtyFieldsRef.current.delete('platforms');
    runRecommend({ toastOnSuccess: true });
  };

  const applyClassification = (cls) => {
    if (!cls) return;
    setClassification(cls);
    if (!dirtyFieldsRef.current.has('selectedGenre') && cls.genre_id) {
      setSelectedGenre(cls.genre_id);
      setGenreUserEdited(false);
    }
  };

  const runClassifyIntake = async ({ silent = true } = {}) => {
    const snap = intakeSnapshotRef.current;
    const text = (snap.text || '').trim();
    const transcript = (snap.transcript || '').trim();
    if (!text && !transcript && !snap.audioUrl) {
      setClassification(null);
      return null;
    }
    setClassifyBusy(true);
    try {
      const { data } = await api.post('/narration/classify-intake', {
        text,
        transcript,
        extracted_text: snap.extractedText || '',
        has_audio: Boolean(snap.audioUrl),
        has_file: Boolean(snap.fileMeta),
        audio_filename: snap.audioName,
        file_filename: snap.fileMeta?.filename || '',
        file_kind: snap.fileMeta?.kind || '',
        audio_duration_seconds: snap.audioDuration || undefined,
        preferred_genre_id: snap.genreDirty ? snap.selectedGenre : '',
        source: snap.intakeMode || 'write',
      });
      const cls = data?.classification || null;
      applyClassification(cls);
      if (!silent && cls?.rationale) toast.message(cls.rationale);
      return cls;
    } catch (err) {
      if (!silent) toast.error(formatApiError(err, 'Could not analyze your input'));
      return null;
    } finally {
      setClassifyBusy(false);
    }
  };

  const scheduleClassify = () => {
    if (classifyTimerRef.current) clearTimeout(classifyTimerRef.current);
    classifyTimerRef.current = setTimeout(() => {
      runClassifyIntake({ silent: true });
    }, 550);
  };

  useEffect(() => {
    return () => {
      if (classifyTimerRef.current) clearTimeout(classifyTimerRef.current);
      try {
        mediaRecorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
    };
  }, []);

  const uploadIntakeBlob = async (blob, filename, source = 'record') => {
    setUploadBusy(true);
    try {
      const data = await uploadIntakeMediaAndCreateStoryTask(blob, {
        source,
        filename: filename || (source === 'mp3' ? 'intake.mp3' : `voice-memo-${Date.now()}.webm`),
      });
      setIntakeAudioUrl(data.s3_url || data.story?.intake_file?.url || '');
      setIntakeAudioName(data.filename || filename || 'audio');
      setIntakeAudioDuration(Number(data.duration_seconds) || 0);
      setIntakeFileMeta({
        filename: data.filename,
        url: data.s3_url,
        storyId: data.story?.id,
        kind: data.file_kind || 'audio',
      });
      if (data.transcript) {
        setIntakeTranscript(data.transcript);
        if (!(intakeText || '').trim()) {
          setIntakeText(data.transcript);
          setCustomGenrePrompt(data.transcript.slice(0, 300));
        }
      }
      toast.success(
        data.transcript ? 'Saved to story task & transcribed' : 'Saved to story task'
      );
      setTimeout(() => runClassifyIntake({ silent: true }), 100);
      return data;
    } catch (err) {
      toast.error(formatApiError(err, 'Audio upload failed'));
      return null;
    } finally {
      setUploadBusy(false);
    }
  };

  const handleIntakeFile = async (file) => {
    if (!file) return;
    await uploadIntakeBlob(file, file.name || 'intake.mp3', 'mp3');
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone not available in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaChunksRef.current = [];
      let options = {};
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          options = { mimeType: 'audio/webm;codecs=opus' };
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          options = { mimeType: 'audio/webm' };
        }
      }
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data?.size) mediaChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(mediaChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        await uploadIntakeBlob(blob, `voice-memo-${Date.now()}.webm`, 'record');
      };
      recorder.start();
      setIsRecording(true);
      toast.message('Recording… tap Stop when done');
    } catch (err) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    setIsRecording(false);
  };

  const handleStep1Next = async () => {
    const text = (intakeText || customGenrePrompt || '').trim();
    if (!text && !intakeAudioUrl && !intakeFileMeta) {
      toast.error('Write an idea/script, record audio, or upload a file / MP3 first');
      return;
    }
    const cls = classification || (await runClassifyIntake({ silent: false }));
    if (!selectedGenre && cls?.genre_id) setSelectedGenre(cls.genre_id);

    const idea = text || intakeTranscript || '';
    setCustomGenrePrompt(idea.slice(0, 300));
    if (idea && !dirtyFieldsRef.current.has('title') && !(title || '').trim()) {
      const sug = cls?.title_suggestion || idea.split('\n')[0].slice(0, 80);
      setTitle(sug);
    }
    if (idea) {
      setShortIdea(idea.slice(0, 500));
      if (!dirtyFieldsRef.current.has('generationBrief')) {
        setGenerationBrief(idea.slice(0, 2000));
      }
    }

    const flowId = cls?.flow_id || 'narration';
    const stay = cls?.stay_in_wizard !== false && flowId === 'narration';
    if (stay) {
      await goToStep(2);
      return;
    }

    const flow =
      STORY_FLOWS.find((f) => f.id === flowId) ||
      STORY_FLOWS.find((f) => f.appRoute === cls?.app_route);
    const route = cls?.app_route || flow?.appRoute;
    if (!route || route === '/narration/create') {
      await goToStep(2);
      return;
    }

    toast.success(`Opening ${cls?.flow_label || flow?.title || 'matching'} flow`);
    navigate(route, {
      state: {
        storyFlow: storyFlowNavigationState(flow),
        quickIntake: {
          text: idea,
          transcript: intakeTranscript,
          audioUrl: intakeAudioUrl,
          audioFilename: intakeAudioName,
          fileUrl: intakeFileMeta?.url,
          storyId: intakeFileMeta?.storyId,
          filename: intakeFileMeta?.filename,
          genreId: selectedGenre || cls?.genre_id,
          contentKind: cls?.content_kind,
          flowId,
          classification: cls,
          recommendedParams: cls?.recommended_params || {},
        },
        recommendedParams: cls?.recommended_params || {},
        prefill: {
          title: cls?.title_suggestion || cls?.recommended_params?.title || '',
          shortIdea: idea.slice(0, 500),
          genreId: selectedGenre || cls?.genre_id || '',
          generationBrief: idea.slice(0, 2000),
          tone: cls?.recommended_params?.tone,
          visualStyle: cls?.recommended_params?.visual_style_ui || cls?.recommended_params?.visual_style,
          ttsProvider: cls?.recommended_params?.tts_provider,
          imageProvider: cls?.recommended_params?.image_provider,
          segmentCount: cls?.recommended_params?.segment_count,
          fullScript: cls?.recommended_params?.full_script,
          newsTopic: cls?.recommended_params?.news_topic,
          pitchScript: cls?.recommended_params?.pitch_script,
          songUrl: intakeAudioUrl || cls?.recommended_params?.song_url,
          tutorialTopic: cls?.recommended_params?.tutorial_topic,
        },
      },
    });
  };

  const goToStep = async (next) => {
    // Auto-suggest when advancing into setup / voice / style / review
    if (
      typeof currentStep === 'number' &&
      typeof next === 'number' &&
      next > currentStep &&
      next <= 5
    ) {
      await runRecommend({ toastOnSuccess: false });
    }
    setCurrentStep(next);
  };


  const pollJob = async (jid, pid) => {
    try {
      const { data: job } = await api.get(`/jobs/${jid}`);
      setJobProgress(job.progress || 0);
      setJobMessage(job.message || 'Processing AI timeline...');

      if (job.status === 'completed') {
        clearInterval(pollRef.current);
        toast.success('Narration video generated!');
        setLoading(false);
        setCurrentStep('final');
      } else if (job.status === 'failed') {
        clearInterval(pollRef.current);
        toast.error(job.error || 'Generation failed');
        setLoading(false);
      }
    } catch {
      // transient network error
    }
  };

  const handleQueueVideo = async () => {
    setCurrentStep(6);
    setQueueStatus('loading');
    setJobProgress(15);
    setQueueError(null);
    setLoading(true);
    try {
      const ratio =
        CREATING_FOR_OPTIONS.find((opt) => opt.id === aspectRatio)?.ratio ||
        (String(aspectRatio).includes('9:16') ? '9:16' : '16:9');
      const activeGenreObj = GENRE_OPTIONS.find((g) => g.id === selectedGenre);
      const topic =
        generationBrief.trim() ||
        shortIdea.trim() ||
        customGenrePrompt.trim() ||
        activeGenreObj?.defaultPrompt ||
        '';

      const payload = buildNarrationCreatePayload({
        title: title.trim() || `${activeGenreObj?.title || 'Narration'} Story`,
        topic,
        tone: tone || activeGenreObj?.defaultTone || 'inspiring',
        visualStyleUi: selectedStyle || 'pixar',
        imageProviderUi: imageProvider || 'nano_banana',
        ttsProvider: selectedVoiceProvider || 'elevenlabs',
        voiceId: selectedVoiceId || selectedVoice.id,
        murfLocale: selectedVoice?.locale || selectedVoice?.language,
        voiceSpeed,
        voicePitch,
        segmentCount: sceneCount,
        aspectRatio: ratio,
        language: language || 'en',
        genreId: selectedGenre,
        autoPublishYoutube: selectedPlatforms.youtube || selectedPlatforms.youtube_shorts,
        autoPublishInstagram: selectedPlatforms.instagram_reels,
      });

      const { data } = await api.post('/narration/projects', payload);
      const nextProjectId = data.project?.id || data.id;
      const nextJobId = data.job_id || data.job?.id;

      setProjectId(nextProjectId);
      setJobId(nextJobId);
      setJobProgress(data.progress || 25);
      setJobMessage(data.message || 'Synthesizing voice narration...');
      setQueuedTimestamp(new Date());
      setQueueStatus('success');
      setLoading(false);
      toast.success('Your video has been added to the publishing queue!');

      if (nextJobId) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => pollJob(nextJobId, nextProjectId), 1200);
      }
      removeNarrationCreateDraft();
    } catch (err) {
      setLoading(false);
      if (handleInsufficientCreditsError(err, openInsufficientCredits)) {
        setQueueStatus('error');
        setQueueError('Insufficient credits to queue video. Please top up your credits.');
      } else {
        const errMsg = formatApiError(err, 'Failed to start narration creation');
        setQueueStatus('error');
        setQueueError(errMsg);
        toast.error(errMsg);
      }
    }
  };

  const currentStepNum = typeof currentStep === 'number' ? currentStep : 6;
  const progressPercent = Math.round((currentStepNum / 6) * 100);

  // Dynamic preview info for Step 6 & summary
  const durationMap = {
    short: '1:30',
    medium: '3:30',
    long: '7:00',
  };
  const displayDuration = durationMap[lengthPreset] || '1:30';
  const displayRatio = CREATING_FOR_OPTIONS.find((opt) => opt.id === aspectRatio)?.ratio || (aspectRatio.includes('9:16') ? '9:16' : '16:9');
  const displayStyle = VISUAL_STYLES.find((s) => s.id === selectedStyle);
  const displayThumbnail = displayStyle?.image || '/flowImages/1.png';
  const activePlatformCount = [
    selectedPlatforms.youtube,
    selectedPlatforms.youtube_shorts,
    selectedPlatforms.instagram_reels,
  ].filter(Boolean).length;
  const displayPlatformCount = activePlatformCount > 0 ? activePlatformCount : 3;

  /* ─────────────────────────────────────────
     Figma Multi-Segment Progress Bar (All 6 Main Steps)
  ───────────────────────────────────────── */
  const renderFigmaProgressPills = (activePillCount) => (
    <div className="flex items-center gap-2 w-full max-w-xl mt-4 sm:mt-5 mb-2">
      {[1, 2, 3, 4, 5, 6].map((pill) => (
        <button
          key={pill}
          type="button"
          onClick={() => setCurrentStep(pill)}
          className={`h-[2.5px] flex-1 rounded-full transition-all duration-300 cursor-pointer ${pill <= activePillCount
              ? 'bg-gradient-to-r from-[#D0BCFF] via-[#7DD3FC] to-[#62FAE3] shadow-[0_0_6px_rgba(98,250,227,0.35)]'
              : 'bg-white/10 hover:bg-white/20'
            }`}
        />
      ))}
    </div>
  );

  const hasNarrationTopic = Boolean(
    generationBrief.trim() ||
      shortIdea.trim() ||
      customGenrePrompt.trim() ||
      GENRE_OPTIONS.find((g) => g.id === selectedGenre)?.defaultPrompt
  );

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden overflow-x-hidden w-full max-w-[100vw] bg-black text-white font-sora flex flex-col justify-between relative selection:bg-[#2DD4BF]/30 narration-flow-page scrollbar-none" style={{ isolation: 'isolate' }}>
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="Narration"
        busy={loading}
        onStart={handleQueueVideo}
        fields={[
          {
            id: 'idea',
            label: 'What should the narration be about?',
            valid: hasNarrationTopic,
            render: () => (
              <Textarea
                value={shortIdea}
                onChange={(e) => {
                  setShortIdea(e.target.value.slice(0, 500));
                  setIntakeText(e.target.value);
                }}
                rows={4}
                placeholder="A short idea or plot for the story…"
                autoFocus
              />
            ),
          },
        ]}
      />
      {/* ── Ambient glows pinned to viewport corners (vibrant & glowing) ── */}
      <div
        className="fixed top-0 left-0 w-[700px] lg:w-[900px] pointer-events-none z-[1] select-none"
        style={{ opacity: 0.60, transform: 'translate(-20%, -20%)' }}
        dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
      />
      <div
        className="fixed bottom-0 right-0 w-[600px] lg:w-[800px] pointer-events-none z-[1] select-none"
        style={{ opacity: 0.60, transform: 'translate(20%, 20%)' }}
        dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }}
      />

      {/* ─────────────────────────────────────────
          Top Navigation Bar (Figma Exact)
      ───────────────────────────────────────── */}
      <header className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-2 flex items-center justify-between border-b border-white/5 shrink-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="h-8 px-3.5 rounded-xl border border-white/10 text-white hover:bg-white/[0.06] hover:text-[#62FAE3] text-xs font-bold flex items-center gap-2 cursor-pointer backdrop-blur-md bg-white/[0.02] transition-all shadow-sm"
          title="Return to Dashboard"
        >
          {currentStep === 6 ? (
            <>
              <ArrowLeft className="w-4 h-4 text-[#62FAE3]" />
              <span className="uppercase tracking-wider font-headline">BACK TO DASHBOARD</span>
            </>
          ) : (
            <>
          <LayoutDashboard className="w-4 h-4 text-[#62FAE3]" />
          <span>Dashboard</span>
            </>
          )}
        </button>

        <div className="flex items-center gap-3">

          {/* Open Editor Button — always accessible for UI preview */}
            <button
            onClick={() => navigate(`/narration/${projectId || 'demo-preview'}/edit`)}
            className="text-[11px] font-mono font-bold tracking-widest text-[#62FAE3] hover:text-white px-3 py-1.5 rounded-xl bg-teal-500/10 border border-teal-500/30 hover:bg-teal-500/20 uppercase cursor-pointer transition-all flex items-center gap-1.5 shadow-[0_0_12px_rgba(98,250,227,0.2)]"
            title="Open Narration Scene Grid Editor"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>OPEN EDITOR</span>
            </button>

          {currentStep === 6 ? (
            <div className="flex items-center gap-2 text-xs font-mono font-bold tracking-wider text-[#2DD4BF] bg-[#2DD4BF]/10 border border-[#2DD4BF]/25 px-3.5 py-1.5 rounded-xl">
              <Layers className="w-4 h-4 text-[#2DD4BF]" />
              <span>VIDEO QUEUE: 3 ACTIVE</span>
          </div>
          ) : null}
        </div>
      </header>

      {/* ─────────────────────────────────────────
          Main Workspace: Left UI (Figma Exact) + Right Roadmap
      ───────────────────────────────────────── */}
      <div className="relative z-[10] w-full max-w-7xl mx-auto px-4 sm:px-6 py-2 flex-1 flex flex-col lg:flex-row gap-6 items-stretch lg:overflow-hidden min-h-0 overflow-x-hidden scrollbar-none">

        {/* LEFT MAIN UI CONTAINER */}
        <main
          onScroll={(e) => {
            const scrolled = e.currentTarget.scrollTop > 8;
            if (scrolled !== isScrolled) setIsScrolled(scrolled);
          }}
          className="w-full flex-1 min-w-0 flex flex-col justify-start lg:overflow-y-auto overflow-x-hidden pr-0.5 scrollbar-none"
        >

          {/* ─────────────────────────────────────────
              STEP 1: What do you want to create? (Figma Exact)
          ───────────────────────────────────────── */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6 pt-2 pb-6"
            >
              {/* Sticky Heading & Progress (Clean transparent) */}
              <div className="sticky top-0 z-30 pt-3 sm:pt-4 pb-2 relative transform-gpu will-change-transform bg-transparent">
                <div className="space-y-3">
                  <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-extrabold tracking-tight text-white font-headline leading-tight">
                  What do you want
                  <span
                      className="block brand-gradient-text mt-1"
                      style={{
                        background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                  >
                    to create?
                  </span>
                </h1>
                  <p className="text-sm sm:text-[15px] text-[#9CA3AF] font-normal max-w-xl font-body leading-relaxed pt-1">
                    Paste a script or idea, record a voice note, or upload an MP3. We detect the type, pick a genre chip, and route you to the right flow — you can edit anytime.
                </p>
                  {renderFigmaProgressPills(1)}
                </div>
              </div>

              {/* Multi-modal intake */}
              <div className="space-y-2">
                <p
                  className="text-[11px] font-medium tracking-[0.18em] text-[#9CA3AF] uppercase"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  YOUR REQUIREMENT
                </p>

                <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 space-y-4 shadow-2xl">
                  {intakeMode === 'write' && (
                    <textarea
                      rows={5}
                      value={intakeText}
                      onChange={(e) => {
                        const v = e.target.value;
                        setIntakeText(v);
                        setCustomGenrePrompt(v.slice(0, 300));
                        intakeSnapshotRef.current = { ...intakeSnapshotRef.current, text: v };
                        scheduleClassify();
                      }}
                      placeholder="e.g. A bedtime story about a fox… or paste a full script / dialogue / news bulletin / lyrics…"
                      className="w-full bg-transparent border-none text-white text-sm placeholder:text-[#4B5563] focus:outline-none resize-none font-body leading-relaxed min-h-[120px]"
                    />
                  )}

                  {intakeMode === 'record' && (
                    <div className="flex flex-col items-center justify-center gap-3 py-4">
                      <button
                        type="button"
                        disabled={uploadBusy}
                        onClick={() => (isRecording ? stopRecording() : startRecording())}
                        className={`w-16 h-16 rounded-full flex items-center justify-center cursor-pointer transition-all ${
                          isRecording
                            ? 'bg-red-500/20 border-2 border-red-400 text-red-300 animate-pulse'
                            : 'bg-[#2DD4BF]/15 border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/25'
                        }`}
                      >
                        {isRecording ? <Square className="w-6 h-6" /> : <Mic className="w-7 h-7" />}
                      </button>
                      <p className="text-xs text-[#9CA3AF] font-body text-center">
                        {isRecording
                          ? 'Recording… tap to stop'
                          : uploadBusy
                            ? 'Uploading / transcribing…'
                            : 'Tap to record a voice note of your idea or script'}
                      </p>
                      {intakeAudioUrl ? (
                        <p className="text-[11px] font-mono text-[#2DD4BF] truncate max-w-full">
                          Saved: {intakeAudioName || 'voice memo'}
                        </p>
                      ) : null}
                    </div>
                  )}

                  {intakeMode === 'upload' && (
                    <div className="flex flex-col items-center justify-center gap-3 py-4">
                      <label className="w-full max-w-md cursor-pointer">
                        <div className="rounded-xl border border-dashed border-white/20 hover:border-[#2DD4BF]/50 bg-white/[0.03] px-4 py-8 text-center transition-colors">
                          {uploadBusy ? (
                            <Loader2 className="w-7 h-7 text-[#2DD4BF] animate-spin mx-auto mb-2" />
                          ) : (
                            <FileAudio className="w-7 h-7 text-[#2DD4BF] mx-auto mb-2" />
                          )}
                          <p className="text-sm text-white font-headline">
                            {uploadBusy ? 'Uploading…' : 'Drop or choose an MP3 / audio file'}
                          </p>
                          <p className="text-[11px] text-[#9CA3AF] mt-1 font-body">
                            Songs → SongAI · Spoken briefs → matched flow after transcript
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg"
                          className="hidden"
                          disabled={uploadBusy}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleIntakeFile(f);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {intakeAudioUrl ? (
                        <p className="text-[11px] font-mono text-[#2DD4BF] truncate max-w-full">
                          {intakeAudioName} {intakeAudioDuration ? `· ${Math.round(intakeAudioDuration)}s` : ''}
                        </p>
                      ) : null}
                    </div>
                  )}

                  {intakeMode === 'file' && (
                    <div className="flex flex-col items-center justify-center gap-3 py-4">
                      <label className="w-full max-w-md cursor-pointer">
                        <div className="rounded-xl border border-dashed border-white/20 hover:border-[#2DD4BF]/50 bg-white/[0.03] px-4 py-8 text-center transition-colors">
                          {uploadBusy ? (
                            <Loader2 className="w-7 h-7 text-[#2DD4BF] animate-spin mx-auto mb-2" />
                          ) : (
                            <FileUp className="w-7 h-7 text-[#2DD4BF] mx-auto mb-2" />
                          )}
                          <p className="text-sm text-white font-headline">
                            {uploadBusy ? 'Uploading to S3…' : 'Upload image, PDF, or document'}
                          </p>
                          <p className="text-[11px] text-[#9CA3AF] mt-1 font-body">
                            Presigned S3 upload · creates a story task on your dashboard
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.txt,.md,.rtf,.csv,.ppt,.pptx,.xls,.xlsx,application/pdf,text/*"
                          className="hidden"
                          disabled={uploadBusy}
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (!f) return;
                            setUploadBusy(true);
                            try {
                              const result = await uploadIntakeMediaAndCreateStoryTask(f, {
                                source: 'file',
                              });
                              setIntakeFileMeta({
                                filename: result.filename,
                                url: result.s3_url,
                                storyId: result.story?.id,
                                kind: result.file_kind,
                              });
                              const body =
                                result.extracted_text ||
                                result.transcript ||
                                `Create a story from this ${result.file_kind}: ${result.filename}`;
                              if (!(intakeText || '').trim() || result.extracted_text) {
                                setIntakeText(body.slice(0, 8000));
                                setCustomGenrePrompt(body.slice(0, 300));
                                intakeSnapshotRef.current = {
                                  ...intakeSnapshotRef.current,
                                  text: body.slice(0, 8000),
                                  extractedText: result.extracted_text || '',
                                  fileMeta: {
                                    filename: result.filename,
                                    url: result.s3_url,
                                    storyId: result.story?.id,
                                    kind: result.file_kind,
                                  },
                                };
                                scheduleClassify();
                              }
                              toast.success(
                                result.extracted_text
                                  ? 'File saved & interpreted'
                                  : 'File uploaded — story task created'
                              );
                            } catch (err) {
                              toast.error(formatApiError(err, 'File upload failed'));
                            } finally {
                              setUploadBusy(false);
                            }
                          }}
                        />
                      </label>
                      {intakeFileMeta ? (
                        <p className="text-[11px] font-mono text-[#2DD4BF] truncate max-w-full">
                          {intakeFileMeta.filename}
                        </p>
                      ) : null}
                      </div>
                  )}

                  {(classification || classifyBusy) && (
                    <div className="rounded-xl border border-[#2DD4BF]/30 bg-[#2DD4BF]/[0.04] p-3 flex items-start gap-2.5">
                      {classifyBusy ? (
                        <Loader2 className="w-4 h-4 text-[#2DD4BF] animate-spin shrink-0 mt-0.5" />
                      ) : classification?.flow_id === 'song_based' ? (
                        <Music2 className="w-4 h-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                      ) : (
                        <TwinneeLogo className="w-4 h-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0 text-xs font-body text-gray-300">
                        <div className="font-bold text-white flex flex-wrap items-center gap-2">
                          <span>AI match</span>
                          {classification ? (
                            <>
                              <span className="px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-mono uppercase">
                                {classification.content_kind}
                              </span>
                              <span className="px-2 py-0.5 rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 text-[10px] font-mono text-[#2DD4BF]">
                                → {classification.flow_label}
                              </span>
                            </>
                          ) : (
                            <span className="text-[#9CA3AF] font-normal">Analyzing…</span>
                          )}
                        </div>
                        {classification?.rationale ? (
                          <p className="text-[#9CA3AF] mt-1 leading-relaxed">{classification.rationale}</p>
                        ) : null}
                      </div>
                    </div>
                  )}

                  <div className="pt-2 flex items-center justify-between border-t border-white/10 gap-3 flex-wrap">
                    <div className="flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/[0.03] flex-wrap">
                      {[
                        { id: 'write', label: 'Write', icon: Edit3 },
                        { id: 'record', label: 'Record', icon: Mic },
                        { id: 'upload', label: 'MP3', icon: Upload },
                        { id: 'file', label: 'File', icon: FileUp },
                      ].map((tab) => {
                        const Icon = tab.icon;
                        const active = intakeMode === tab.id;
                        return (
                          <button
                            key={tab.id}
                            type="button"
                            onClick={() => setIntakeMode(tab.id)}
                            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 cursor-pointer transition-all ${
                              active
                                ? 'bg-white/15 text-white border border-white/20'
                                : 'text-[#9CA3AF] hover:text-white'
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {tab.label}
                          </button>
                    );
                  })}
              </div>

                    <button
                      type="button"
                      onClick={handleStep1Next}
                      disabled={recommendBusy || classifyBusy || uploadBusy || isRecording}
                      className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs sm:text-sm font-headline tracking-wider hover:brightness-105 active:scale-95 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center gap-1.5 disabled:opacity-60"
                    >
                      <span>
                        {classifyBusy || uploadBusy
                          ? 'Working…'
                          : classification?.stay_in_wizard === false
                            ? `Continue in ${classification.flow_label}`
                            : 'Next'}
                      </span>
                      <span>→</span>
                    </button>
                  </div>
                </div>
                </div>

              {/* Genre Cards Grid — auto-highlighted from intake; user can override */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-mono font-medium tracking-[0.18em] text-[#9CA3AF] uppercase">
                    SELECT GENRE
                  </p>
                  {selectedGenre && classification?.genre_id === selectedGenre && !genreUserEdited ? (
                    <span className="text-[10px] font-mono text-[#2DD4BF]">Suggested from your input</span>
                  ) : null}
                    </div>

                <div className="flex flex-wrap gap-2">
                  {GENRE_OPTIONS.map((genre) => {
                    const Icon = genre.icon;
                    const isSelected = selectedGenre === genre.id;
                    return (
                    <button
                        key={genre.id}
                        type="button"
                        title={genre.subtitle}
                        onClick={() => {
                          markDirty('selectedGenre');
                          setGenreUserEdited(true);
                          setSelectedGenre(genre.id);
                          scheduleClassify();
                        }}
                        className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold font-body transition-all duration-150 cursor-pointer backdrop-blur-md ${
                          isSelected
                            ? 'border border-[#2DD4BF] bg-[#2DD4BF]/15 text-white shadow-[0_0_14px_rgba(45,212,191,0.2)]'
                            : 'border border-white/10 bg-white/[0.03] text-[#D1D5DB] hover:border-white/25 hover:text-white hover:bg-white/[0.06]'
                        }`}
                      >
                        <Icon className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-[#2DD4BF]' : 'text-[#9CA3AF]'}`} />
                        <span>{genre.title}</span>
                        {isSelected ? (
                          <span className="ml-0.5 w-3.5 h-3.5 rounded-full bg-[#2DD4BF] flex items-center justify-center shrink-0">
                            <Check className="w-2 h-2 text-[#06080D] stroke-[3]" />
                          </span>
                        ) : null}
                    </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────────────────────────────
              STEP 2: Create your narration story (Figma Exact)
          ───────────────────────────────────────── */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6 pt-2 pb-6"
            >
              {/* Sticky Heading & Progress (Clean transparent) */}
              <div className="sticky top-0 z-30 pt-3 sm:pt-4 pb-2 relative transform-gpu will-change-transform bg-transparent">
                <div className="space-y-3">
                  <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-extrabold tracking-tight text-white font-headline leading-tight">
                  <span
                      className="block brand-gradient-text"
                      style={{
                        background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                  >
                    Create your
                  </span>
                    <span className="block mt-1">narration story</span>
                </h1>
                  <p className="text-sm sm:text-[15px] text-[#9CA3AF] font-normal max-w-xl font-body leading-relaxed pt-1">
                  Click a genre, answer a few questions — we pick voice, visuals, and providers. You can edit anything before we queue the video.
                </p>
                  {renderFigmaProgressPills(2)}
                </div>
              </div>

              {/* Story Form Card (Transparent & Glassmorphic) */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-6 sm:p-7 space-y-5 shadow-2xl">
                {/* Title */}
                <div className="space-y-1.5">
                  <label
                    className="text-[11px] font-medium tracking-[0.18em] text-[#9CA3AF] uppercase block"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    TITLE
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Give your video a working title"
                    className="w-full h-12 rounded-xl bg-white/[0.03] border border-white/10 px-4 text-white text-sm placeholder:text-[#4B5563] focus:border-[#2DD4BF]/60 focus:bg-white/[0.06] focus:outline-none transition-all font-body"
                  />
                </div>

                {/* Short Idea */}
                <div className="space-y-1.5">
                  <label
                    className="text-[11px] font-medium tracking-[0.18em] text-[#9CA3AF] uppercase block"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    SHORT IDEA <span className="text-[#6B7280]">(OPTIONAL)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={shortIdea}
                    onChange={(e) => setShortIdea(e.target.value)}
                    placeholder="One or two sentences — who, where, what happens"
                    className="w-full rounded-xl bg-white/[0.03] border border-white/10 p-4 text-white text-sm placeholder:text-[#4B5563] focus:border-[#2DD4BF]/60 focus:bg-white/[0.06] focus:outline-none transition-all font-body resize-none"
                  />
                </div>

                {/* 2-Column: Language & Length */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label
                      className="text-[11px] font-medium tracking-[0.18em] text-[#9CA3AF] uppercase block"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      LANGUAGE
                    </label>
                    <div className="relative">
                      <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full h-12 rounded-xl bg-white/[0.03] border border-white/10 px-4 text-white text-sm focus:border-[#2DD4BF]/60 focus:bg-white/[0.06] focus:outline-none appearance-none font-body cursor-pointer"
                      >
                        <option value="" disabled className="bg-[#0B0F17] text-gray-500">
                          Select language
                        </option>
                        {LANGUAGES.map((l) => (
                          <option key={l.code} value={l.label} className="bg-[#0B0F17] text-white">
                            {l.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-[#9CA3AF] absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label
                      className="text-[11px] font-medium tracking-[0.18em] text-[#9CA3AF] uppercase block"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      LENGTH
                    </label>
                    <div className="grid grid-cols-3 gap-1 bg-white/[0.03] border border-white/10 p-1 rounded-xl min-h-[48px] h-12">
                      {LENGTH_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handleLengthChange(opt.id)}
                          className={`rounded-lg py-1 px-1.5 transition-all cursor-pointer flex flex-col items-center justify-center text-center ${lengthPreset === opt.id
                            ? 'bg-white/15 text-white border border-white/20 shadow-sm'
                            : 'text-[#9CA3AF] hover:text-white hover:bg-white/[0.04]'
                            }`}
                        >
                          <span className="text-xs font-semibold font-body leading-tight">{opt.label}</span>
                          <span className={`text-[10px] font-mono leading-tight ${lengthPreset === opt.id ? 'text-[#62FAE3]' : 'text-[#9CA3AF]'}`}>
                            {opt.duration}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* I Want to Create Selection (Selectable Option Cards) */}
                <div className="space-y-2">
                  <label
                    className="text-[11px] font-medium tracking-[0.18em] text-[#9CA3AF] uppercase block"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    I WANT TO CREATE...
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {CREATING_FOR_OPTIONS.map((opt) => {
                      const isSelected = aspectRatio === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setAspectRatio(opt.id)}
                          className={`p-3 rounded-xl border text-left transition-all duration-200 cursor-pointer flex items-center justify-between gap-2.5 backdrop-blur-md ${isSelected
                            ? 'border-[#2DD4BF] bg-[#2DD4BF]/10 text-white shadow-[0_0_20px_rgba(45,212,191,0.15)]'
                            : 'border-white/10 bg-white/[0.02] text-[#D1D5DB] hover:text-white hover:bg-white/[0.05] hover:border-white/20'
                            }`}
                        >
                          <div className="min-w-0 flex-1">
                            <h4 className="text-xs sm:text-[13px] font-semibold truncate font-headline">{opt.title}</h4>
                          </div>
                          <FigmaRadioTick selected={isSelected} className="w-4 h-4 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Card Bottom Row: Step Pill + Gradient Next Button */}
                <div className="pt-3 flex items-center justify-between border-t border-white/10">
                  <div className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-xs font-mono text-[#9CA3AF] flex items-center gap-2">
                    <span>Step 2 of 6</span>
                    <span>•</span>
                    <span>Advanced narration</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="px-5 py-2.5 rounded-full border border-white/15 text-[#9CA3AF] font-semibold text-xs hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => goToStep(3)}
                      className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs sm:text-sm font-headline tracking-wider hover:brightness-105 active:scale-95 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center gap-1.5"
                    >
                      <span>Next</span>
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────────────────────────────
              STEP 3: Voice Configuration (Figma Exact)
          ───────────────────────────────────────── */}
          {currentStep === 3 && (
            <motion.div
              key="step3-voice"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6 pt-2 pb-6"
            >
              {/* Sticky Heading & Progress (Clean transparent) */}
              <div className="sticky top-0 z-30 pt-3 sm:pt-4 pb-2 relative transform-gpu will-change-transform bg-transparent">
                <div className="space-y-3">
                  <h1 className="text-3xl sm:text-4xl lg:text-[40px] font-extrabold tracking-tight text-white font-headline leading-tight">
                  Voice{' '}
                  <span
                    className="bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }}
                  >
                    Configuration
                  </span>
                </h1>
                  <p className="text-sm sm:text-[15px] text-[#9CA3AF] font-normal font-body pt-1">
                  Choose the perfect voice for your story.
                </p>
                  {renderFigmaProgressPills(3)}
                </div>
              </div>

              {/* Sub-panels: Left Voices (lg:col-span-8) + Right Audio Controls (lg:col-span-4) */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                {/* Voice Cards (Left Column) */}
                <div className="lg:col-span-8 space-y-3">
                  {/* Top Provider + Gender Filter Bar Card (Figma Exact) */}
                  <div className="p-2 sm:p-2.5 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex flex-wrap items-center justify-between gap-3 shadow-md">
                    {/* Provider Tabs inside pill container */}
                    <div className="p-1 rounded-full border border-white/10 bg-white/[0.03] flex items-center gap-1">
                      {VOICE_PROVIDERS.map((provider) => (
                        <button
                          key={provider.id}
                          onClick={() => setSelectedVoiceProvider(provider.id)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${selectedVoiceProvider === provider.id
                            ? 'bg-white/15 border border-white/20 text-white shadow-sm'
                            : 'text-[#9CA3AF] hover:text-white'
                            }`}
                        >
                          {provider.name}
                        </button>
                      ))}
                    </div>

                    {/* Gender & Language Pills */}
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedVoiceGender}
                        onChange={(e) => setSelectedVoiceGender(e.target.value)}
                        className="px-3.5 py-1.5 rounded-xl text-xs bg-white/[0.04] border border-white/10 text-white focus:outline-none cursor-pointer"
                      >
                        <option value="all" className="bg-[#0B0F17] text-white">All Genders</option>
                        <option value="male" className="bg-[#0B0F17] text-white">Male</option>
                        <option value="female" className="bg-[#0B0F17] text-white">Female</option>
                      </select>

                      <select
                        value={selectedVoiceLanguage}
                        onChange={(e) => setSelectedVoiceLanguage(e.target.value)}
                        className="px-3.5 py-1.5 rounded-xl text-xs bg-white/[0.04] border border-white/10 text-white focus:outline-none cursor-pointer"
                      >
                        <option value="all" className="bg-[#0B0F17] text-white">English (US)</option>
                        <option value="Hindi" className="bg-[#0B0F17] text-white">Hindi</option>
                      </select>
                    </div>
                  </div>

                  {/* 3 Voice Cards Top Row + Aria Below / "+ More Voices" */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {filteredVoices.slice(0, 5).map((voice) => {
                      const isSelected = selectedVoiceId === voice.id;
                      const isCardPlaying = isPlayingVoice && playingVoiceCardId === voice.id;

                      return (
                        <div
                          key={voice.id}
                          onClick={() => setSelectedVoiceId(voice.id)}
                          className={`relative rounded-xl p-3 transition-all duration-200 cursor-pointer flex flex-col items-center justify-between text-center min-h-[140px] backdrop-blur-md ${isSelected
                            ? 'border-2 border-[#2DD4BF] bg-[#2DD4BF]/10 shadow-[0_0_20px_rgba(45,212,191,0.15)]'
                            : 'border border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.05]'
                            }`}
                        >
                          <div className="flex flex-col items-center space-y-1.5">
                            <div
                              className={`w-9 h-9 rounded-full overflow-hidden p-0.5 transition-all ${isSelected
                                ? 'border-2 border-[#2DD4BF] shadow-[0_0_8px_#2DD4BF]'
                                : 'border border-white/20'
                                }`}
                            >
                              <img
                                src={voice.avatar}
                                alt={voice.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            </div>

                            <div>
                              <h3 className="text-xs font-bold text-white font-headline leading-tight">
                                {voice.name}
                              </h3>
                              <p className="text-[10px] text-[#2DD4BF] font-mono mt-0.5 tracking-tight">
                                {voice.tagline}
                              </p>
                            </div>
                          </div>

                          <div className="w-full space-y-1.5 mt-1.5 pt-1.5 border-t border-white/[0.06]">
                            {/* Scrubber 0:42 --- 1:30 */}
                            <div className="flex items-center justify-between text-[8px] font-mono text-[#6B7280]">
                              <span>0:42</span>
                              <div className="flex-1 mx-1.5 h-[2px] bg-white/10 rounded-full relative overflow-visible">
                                <div
                                  className={`h-full rounded-full ${isSelected || isCardPlaying ? 'bg-[#2DD4BF]' : 'bg-white/30'
                                    }`}
                                  style={{
                                    width: isCardPlaying
                                      ? `${(samplePlayingTime / 12) * 100}%`
                                      : isSelected
                                        ? '45%'
                                        : '35%',
                                  }}
                                />
                                <div
                                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected || isCardPlaying
                                    ? 'bg-[#2DD4BF] shadow-[0_0_6px_#2DD4BF]'
                                    : 'bg-white/40'
                                    }`}
                                  style={{
                                    left: isCardPlaying
                                      ? `${(samplePlayingTime / 12) * 100}%`
                                      : isSelected
                                        ? '45%'
                                        : '35%',
                                  }}
                                />
                              </div>
                              <span>1:30</span>
                            </div>

                            {/* Center Circular Play Button */}
                            <div className="flex justify-center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTogglePlayVoice(voice);
                                }}
                                className={`w-6 h-6 rounded-full flex items-center justify-center transition-all cursor-pointer ${isCardPlaying
                                  ? 'bg-[#2DD4BF] text-[#06080d] shadow-[0_0_8px_#2DD4BF]'
                                  : isSelected
                                    ? 'bg-[#2DD4BF] text-[#06080d] shadow-[0_0_6px_#2DD4BF]'
                                    : 'bg-white/10 hover:bg-white/20 text-white'
                                  }`}
                              >
                                {isCardPlaying ? (
                                  <Pause className="w-2.5 h-2.5 fill-current" />
                                ) : isSelected ? (
                                  <Pause className="w-2.5 h-2.5 fill-current" />
                                ) : (
                                  <Play className="w-2.5 h-2.5 fill-current translate-x-0.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* "+ More Voices" Card */}
                    <div
                      onClick={() => setIsVoicesModalOpen(true)}
                      className="rounded-xl border-2 border-dashed border-[#2DD4BF]/40 hover:border-[#2DD4BF] bg-[#2DD4BF]/[0.03] hover:bg-[#2DD4BF]/[0.08] backdrop-blur-md transition-all p-3 flex flex-col items-center justify-center text-center cursor-pointer group min-h-[140px]"
                    >
                      <div className="w-7 h-7 rounded-full bg-[#2DD4BF]/10 border border-[#2DD4BF]/30 group-hover:bg-[#2DD4BF] group-hover:text-[#06080d] text-[#2DD4BF] flex items-center justify-center transition-all mb-1">
                        <TwinneeLogo className="w-3.5 h-3.5" />
                      </div>
                      <h4 className="text-xs font-bold text-white font-headline group-hover:text-[#2DD4BF]">
                        + More Voices
                      </h4>
                      <p className="text-[9px] text-[#9CA3AF] font-body mt-0.5">
                        Browse all library voices
                      </p>
                    </div>
                  </div>
                </div>

                {/* Audio Controls Panel (Right Column - Ultra Compact Glass) */}
                <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-3.5 space-y-2 shadow-xl">
                  <div>
                    <h3 className="text-xs font-bold text-white font-headline">Audio Controls</h3>
                    <p className="text-[10px] text-[#9CA3AF] font-body mt-0.5">Fine-tune {selectedVoice.name}'s delivery.</p>
                  </div>

                  <div className="space-y-0.5">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-[#9CA3AF] font-body">Speed</span>
                      <span className="font-mono font-bold text-[#2DD4BF] text-[11px]">{voiceSpeed.toFixed(1)}x</span>
                    </div>
                    <input
                      type="range"
                      min={0.5}
                      max={2.0}
                      step={0.1}
                      value={voiceSpeed}
                      onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))}
                      className="w-full accent-[#2DD4BF] bg-white/10 h-1 rounded-lg cursor-pointer"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-[#9CA3AF] font-body">Pitch</span>
                      <span className="font-mono font-bold text-[#2DD4BF] text-[11px]">{voicePitch}</span>
                    </div>
                    <input
                      type="range"
                      min={-10}
                      max={10}
                      step={1}
                      value={voicePitch}
                      onChange={(e) => setVoicePitch(parseInt(e.target.value, 10))}
                      className="w-full accent-[#2DD4BF] bg-white/10 h-1 rounded-lg cursor-pointer"
                    />
                  </div>

                  <div className="border-t border-white/10 pt-1.5 space-y-1">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-[#9CA3AF] font-body">Sample Playback</span>
                      <span className="font-mono text-[9px] text-[#6B7280]">
                        {isPlayingVoice ? `0:0${samplePlayingTime} / 0:12` : '0:04 / 0:12'}
                      </span>
                    </div>

                    {/* Animated Waveform Equalizer (Compact) */}
                    <div
                      onClick={() => handleTogglePlayVoice(selectedVoice)}
                      className="h-6 rounded-lg bg-transparent flex items-center justify-center gap-1 cursor-pointer"
                    >
                      {[30, 50, 80, 95, 60, 85, 100, 70, 45, 90, 75, 55, 100, 85, 65, 90].map(
                        (baseHeight, i) => (
                          <div
                            key={i}
                            className={`w-[2.5px] rounded-full transition-all duration-150 ${isPlayingVoice
                              ? 'bg-[#2DD4BF] shadow-[0_0_5px_rgba(45,212,191,0.6)]'
                              : 'bg-[#2DD4BF]/40'
                              }`}
                            style={{
                              height: isPlayingVoice
                                ? `${Math.max(15, ((baseHeight * ((samplePlayingTime + i) % 4 + 1)) / 4.5))}%`
                                : `${baseHeight * 0.35}%`,
                            }}
                          />
                        )
                      )}
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-1.5">
                    <button
                      onClick={() => {
                        toast.success(`Voice applied: ${selectedVoice.name}`);
                        setCurrentStep(4);
                      }}
                      className="w-full py-1.5 rounded-lg border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/10 font-bold text-[11px] font-headline tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Check className="w-3 h-3 stroke-[2.5]" />
                      <span>Apply Voice</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Bottom Footer Actions */}
              <div className="pt-3 flex items-center justify-between border-t border-white/10">
                <div className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-xs font-mono text-[#9CA3AF] flex items-center gap-2">
                  <span>Step 3 of 6</span>
                  <span>•</span>
                  <span>Voice Configuration</span>
                </div>

                <div className="flex items-center gap-3">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-5 py-2.5 rounded-full border border-white/15 text-[#9CA3AF] font-semibold text-xs hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                >
                  Back
                </button>
                <button
                    onClick={() => goToStep(4)}
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs sm:text-sm font-headline tracking-wider hover:brightness-105 active:scale-95 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center gap-1.5"
                >
                  <span>Next</span>
                  <span>→</span>
                </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────────────────────────────
              STEP 4: Your script, verbatim (Figma Exact)
          ───────────────────────────────────────── */}
          {currentStep === 4 && (
            <motion.div
              key="step4-style"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6 pt-2 pb-6"
            >
              {/* Sticky Heading & Progress (Clean transparent) */}
              <div className="sticky top-0 z-30 pt-3 sm:pt-4 pb-2 relative transform-gpu will-change-transform bg-transparent">
                <div className="space-y-3">
                  <h1 className="text-3xl sm:text-4xl lg:text-[40px] font-extrabold tracking-tight text-white font-headline leading-tight">
                  <span className="bg-gradient-to-r from-[#A5B4FC] to-[#67E8F9] bg-clip-text text-transparent">
                    Your script,
                  </span>{' '}
                  verbatim
                </h1>
                  <p className="text-sm sm:text-[15px] text-[#9CA3AF] font-normal max-w-2xl font-body leading-relaxed pt-1">
                  Paste a finished script. We split it into contextual scene beats (verbatim text, ~10–18s each) so every numbered segment can map to one generated image, then TTS + WhisperX timing.
                </p>
              {renderFigmaProgressPills(4)}
                </div>
              </div>


              {/* Visual Style Grid (Figma Exact: 6 Cards in 3x2) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-white font-headline">Visual style</h2>
                    <p className="text-xs text-[#9CA3AF] font-body">Tap a look — preview shows the art direction</p>
                  </div>
                  <span className="text-[10px] font-mono tracking-wider text-[#D0BCFF] bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 px-3 py-1 rounded-full uppercase">
                    PHOTOREAL / CINEMATIC REAL
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {VISUAL_STYLES.map((style) => {
                    const isSelected = selectedStyle === style.id;
                    return (
                      <div
                        key={style.id}
                        onClick={() => setSelectedStyle(style.id)}
                        className="cursor-pointer group"
                      >
                        <div
                          className={`relative rounded-xl overflow-hidden border transition-all duration-200 backdrop-blur-md ${isSelected
                            ? 'border-2 border-[#2DD4BF] shadow-[0_0_20px_rgba(45,212,191,0.2)]'
                            : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                            }`}
                        >
                          <div className="aspect-[16/9] w-full relative overflow-hidden bg-black/40">
                            <img
                              src={style.image}
                              alt={style.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <span className="absolute bottom-2 left-2 text-[9px] font-mono font-bold tracking-wider text-white bg-black/60 px-2 py-0.5 rounded uppercase">
                              {style.badge}
                            </span>
                            {isSelected && (
                              <div className="absolute top-2 right-2">
                                <FigmaPurpleTickBadge />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-1.5 px-0.5">
                          <span className="text-xs font-semibold text-white font-body">{style.name}</span>
                          <FigmaRadioTick selected={isSelected} className="w-3.5 h-3.5" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 2-Input Settings Row (Transparent Glass) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-[#9CA3AF] uppercase block">Image provider</label>
                  <select
                    value={imageProvider}
                    onChange={(e) => setImageProvider(e.target.value)}
                    className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 px-3 text-white text-xs focus:border-[#2DD4BF]/60 focus:bg-white/[0.06] focus:outline-none appearance-none font-body cursor-pointer"
                  >
                    <option value="" disabled className="bg-[#0B0F17] text-gray-500">
                      Select provider
                    </option>
                    {IMAGE_PROVIDERS.map((p) => (
                      <option key={p.id} value={p.id} className="bg-[#0B0F17] text-white">
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-[#9CA3AF] uppercase block">I want to create</label>
                  <select
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 px-3 text-white text-xs focus:border-[#2DD4BF]/60 focus:bg-white/[0.06] focus:outline-none appearance-none font-body cursor-pointer"
                  >
                    {CREATING_FOR_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id} className="bg-[#0B0F17] text-white">
                        {opt.title}
                      </option>
                    ))}
                  </select>
                </div>
                </div>


              {/* Bottom Footer Actions (Figma Exact) */}
              <div className="pt-3 flex items-center justify-between border-t border-white/10">
                <div className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-xs font-mono text-[#9CA3AF] flex items-center gap-2">
                  <span>Step 4 of 6</span>
                  <span>•</span>
                  <span>Visual Style</span>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => goToStep(3)}
                    className="px-5 py-2.5 rounded-full border border-white/15 text-[#9CA3AF] font-semibold text-xs hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => goToStep(5)}
                    className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs sm:text-sm font-headline tracking-wider hover:brightness-105 active:scale-95 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center gap-1.5"
                  >
                    <span>Continue to Summary</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────────────────────────────
              STEP 5: Summary & Publish (Figma Exact)
          ───────────────────────────────────────── */}
          {currentStep === 5 && (
            <motion.div
              key="step5-summary-publish"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-5 pt-2 pb-6 w-full max-w-6xl mx-auto"
            >
              {/* Sticky Heading & Progress (Clean transparent) */}
              <div className="sticky top-0 z-30 pt-3 sm:pt-4 pb-2 relative transform-gpu will-change-transform bg-transparent">
                <div className="space-y-2.5">
                  <h1 className="text-3xl sm:text-4xl lg:text-[40px] font-extrabold tracking-tight text-white font-headline leading-tight">
                    Review &{' '}
                      <span
                        className="bg-clip-text text-transparent"
                        style={{ backgroundImage: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }}
                      >
                      Publish
                    </span>
                    </h1>
                  <p className="text-sm sm:text-[15px] text-[#9CA3AF] font-normal font-body pt-0.5">
                    Review your narrative parameters and choose your publish destinations before rendering.
                    </p>
                  {renderFigmaProgressPills(5)}
                </div>
                  </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                {/* Left Column: Summary & Generation Parameters */}
                <div className="lg:col-span-6 space-y-4">
                  {/* Story Summary Card */}
                  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-5 space-y-3 shadow-[0_4px_30px_rgba(0,0,0,0.3)]">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2DD4BF]/10 border border-[#2DD4BF]/25 text-[#62FAE3] text-xs font-mono font-semibold">
                      <TwinneeLogo className="w-3.5 h-3.5" />
                      <span>Story Summary</span>
                    </div>

                    <p className="text-xs sm:text-sm text-slate-300 font-body leading-relaxed">
                      "{generationBrief || shortIdea || customGenrePrompt || 'A captivating narrative crafted with emotional pacing and coherent character rendering.'}"
                    </p>
                </div>

                  {/* Generation Parameters List */}
                  <div className="space-y-2.5">
                  <p
                      className="text-[11px] font-mono font-bold tracking-[0.2em] text-[#9CA3AF] uppercase"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    GENERATION PARAMETERS
                  </p>

                  {/* Parameter Card 1: Genre */}
                    <div className="p-3.5 sm:p-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl flex items-center gap-3.5 hover:border-white/20 hover:bg-white/[0.04] transition-all shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
                      <div className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-gray-300 shrink-0">
                        <BookOpen className="w-4.5 h-4.5 text-white" />
                    </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[11px] text-[#9CA3AF] block font-medium">Genre</span>
                        <h4 className="text-sm font-bold text-white font-headline capitalize truncate">
                        {GENRE_OPTIONS.find((g) => g.id === selectedGenre)?.title || selectedGenre || 'Bedtime Story'}
                      </h4>
                    </div>
                  </div>

                    {/* Parameter Card 2: Visual Style & Ratio */}
                    <div className="p-3.5 sm:p-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl flex items-center gap-3.5 hover:border-white/20 hover:bg-white/[0.04] transition-all shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
                      <div className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-gray-300 shrink-0">
                        <Palette className="w-4.5 h-4.5 text-white" />
                    </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[11px] text-[#9CA3AF] block font-medium">Visual Style & Canvas</span>
                        <h4 className="text-sm font-bold text-white font-headline flex items-center gap-2 truncate">
                        <span>{VISUAL_STYLES.find((s) => s.id === selectedStyle)?.name || selectedStyle || 'Pixar / Toy Story'}</span>
                        <span className="text-xs text-[#9CA3AF] font-mono font-medium">
                          {CREATING_FOR_OPTIONS.find((opt) => opt.id === aspectRatio)?.ratio || '16:9'}
                        </span>
                      </h4>
                    </div>
                  </div>

                    {/* Parameter Card 3: Voice & Audio */}
                    <div className="p-3.5 sm:p-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl flex items-center gap-3.5 hover:border-white/20 hover:bg-white/[0.04] transition-all shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
                      <div className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-gray-300 shrink-0">
                        <Mic className="w-4.5 h-4.5 text-white" />
                    </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[11px] text-[#9CA3AF] block font-medium">Voice & Narration</span>
                        <h4 className="text-sm font-bold text-white font-headline flex items-center gap-2 truncate">
                        <span>{selectedVoice.name || 'Elias'}</span>
                          <span className="text-xs text-[#9CA3AF] font-normal font-body truncate">
                            {selectedVoice.tagline || 'Narrative Voice'}
                        </span>
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

                {/* Right Column: Publish Platforms & Queue Card */}
                <div className="lg:col-span-6 space-y-4">
                  <div className="space-y-2.5">
                    <p
                      className="text-[11px] font-mono font-bold tracking-[0.2em] text-[#9CA3AF] uppercase"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      PUBLISH PLATFORMS
                    </p>

                <div className="space-y-2.5">
                  {/* YouTube */}
                  <div
                    onClick={() => handleTogglePlatform('youtube')}
                        className={`p-3.5 rounded-2xl border transition-all duration-200 flex items-center justify-between cursor-pointer backdrop-blur-md ${selectedPlatforms.youtube
                          ? 'border-[#2DD4BF] bg-[#2DD4BF]/10 shadow-[0_0_15px_rgba(45,212,191,0.15)]'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                      }`}
                  >
                        <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-teal-500/10 border border-teal-500/20 text-[#2DD4BF] flex items-center justify-center">
                            <Youtube className="w-4.5 h-4.5" />
                      </div>
                      <div>
                            <h4 className="text-xs sm:text-sm font-bold text-white font-headline">YouTube</h4>
                            <p className="text-[11px] text-[#9CA3AF] font-body">Long-form horizontal video (16:9)</p>
                      </div>
                    </div>
                        <FigmaRadioTick selected={selectedPlatforms.youtube} className="w-4.5 h-4.5" />
                  </div>

                  {/* YouTube Shorts */}
                  <div
                    onClick={() => handleTogglePlatform('youtube_shorts')}
                        className={`p-3.5 rounded-2xl border transition-all duration-200 flex items-center justify-between cursor-pointer backdrop-blur-md ${selectedPlatforms.youtube_shorts
                          ? 'border-[#2DD4BF] bg-[#2DD4BF]/10 shadow-[0_0_15px_rgba(45,212,191,0.15)]'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                      }`}
                  >
                        <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-teal-500/10 border border-teal-500/20 text-[#2DD4BF] flex items-center justify-center">
                            <Zap className="w-4.5 h-4.5" />
                      </div>
                      <div>
                            <h4 className="text-xs sm:text-sm font-bold text-white font-headline">YouTube Shorts</h4>
                            <p className="text-[11px] text-[#9CA3AF] font-body">Snappy vertical clips (9:16)</p>
                      </div>
                    </div>
                        <FigmaRadioTick selected={selectedPlatforms.youtube_shorts} className="w-4.5 h-4.5" />
                  </div>

                  {/* Instagram Reels */}
                  <div
                    onClick={() => handleTogglePlatform('instagram_reels')}
                        className={`p-3.5 rounded-2xl border transition-all duration-200 flex items-center justify-between cursor-pointer backdrop-blur-md ${selectedPlatforms.instagram_reels
                          ? 'border-[#2DD4BF] bg-[#2DD4BF]/10 shadow-[0_0_15px_rgba(45,212,191,0.15)]'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                      }`}
                  >
                        <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-teal-500/10 border border-teal-500/20 text-[#2DD4BF] flex items-center justify-center">
                            <Instagram className="w-4.5 h-4.5" />
                      </div>
                      <div>
                            <h4 className="text-xs sm:text-sm font-bold text-white font-headline">Instagram Reels</h4>
                            <p className="text-[11px] text-[#9CA3AF] font-body">Social media vertical format (9:16)</p>
                      </div>
                    </div>
                        <FigmaRadioTick selected={selectedPlatforms.instagram_reels} className="w-4.5 h-4.5" />
                  </div>
                </div>

                    <p className="text-[11px] text-[#6B7280] font-body">
                      Skip publish — we still save your narration video and provide download & edit access.
                </p>
              </div>

                  {/* Ready to Queue Card */}
              <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] backdrop-blur-md p-4 flex items-center gap-3.5 shadow-lg">
                <div className="w-9 h-9 rounded-xl bg-teal-500/10 border border-teal-500/20 text-[#2DD4BF] flex items-center justify-center shrink-0">
                  <Clapperboard className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                      <h4 className="text-xs sm:text-sm font-bold text-white font-headline mb-0.5">Ready to queue</h4>
                      <div className="flex flex-wrap items-center gap-2.5 text-[11px] font-mono text-[#9CA3AF]">
                        <span>🎬 {title || 'Narration Story'}</span>
                        <span>✦ {VISUAL_STYLES.find((s) => s.id === selectedStyle)?.name || 'Pixar'}</span>
                        <span>👤 {selectedVoice.name || 'AI Voice'}</span>
                    <span>⛶ {CREATING_FOR_OPTIONS.find((opt) => opt.id === aspectRatio)?.ratio || '16:9'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Footer Row */}
              <div className="pt-3 flex items-center justify-between border-t border-white/10">
                <div className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-xs font-mono text-[#9CA3AF] flex items-center gap-2">
                  <span>Step 5 of 6</span>
                  <span>•</span>
                  <span>Summary & Publish</span>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => goToStep(4)}
                    className="px-5 py-2.5 rounded-full border border-white/15 text-[#9CA3AF] font-semibold text-xs hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleQueueVideo}
                    disabled={loading}
                    className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs sm:text-sm font-headline tracking-wider hover:brightness-105 active:scale-95 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TwinneeLogo className="w-4 h-4" />
                    )}
                    <span>Queue video</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────────────────────────────
              STEP 6: Video Queued Process (Figma Exact)
          ───────────────────────────────────────── */}
          {currentStep === 6 && (
            <motion.div
              key="step6-queued-process"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-2xl mx-auto py-2 sm:py-4 flex flex-col items-center justify-center my-auto"
            >
              {queueStatus === 'idle' ? (
                /* ── Direct Access / Idle State (When user opens Step 6 directly) ── */
                <div className="w-full rounded-3xl border border-white/10 bg-[#091114]/90 backdrop-blur-2xl p-8 sm:p-12 shadow-2xl text-center relative overflow-hidden space-y-6">
                  <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto shadow-inner text-[#62FAE3]">
                    <Film className="w-8 h-8 opacity-80" />
                  </div>

                  <div className="space-y-2 max-w-md mx-auto">
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-headline tracking-tight">
                      This is <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }}>Queued Page Only</span>
                    </h2>
                    <p className="text-xs sm:text-sm text-[#9CA3AF] font-body leading-relaxed">
                      No video is currently queued. To generate and queue your video, please complete your story setup in Step 5 and click <strong className="text-white font-medium">&ldquo;Queue video&rdquo;</strong>.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-sm mx-auto pt-2">
                    <button
                      type="button"
                      onClick={() => goToStep(5)}
                      className="py-3 px-4 rounded-2xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs font-headline tracking-wide hover:brightness-105 active:scale-98 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <span>Go to Step 5</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="py-3 px-4 rounded-2xl bg-white/[0.06] hover:bg-white/10 border border-white/15 text-white font-semibold text-xs font-headline active:scale-98 transition-all cursor-pointer flex items-center justify-center"
                    >
                      <span>Dashboard</span>
                    </button>
                  </div>
                </div>
              ) : queueStatus === 'loading' ? (
                /* ── Loading / Queueing State (With Exact Circular Loader) ── */
                <div className="w-full space-y-3.5 text-left animate-in fade-in duration-300">
                  {/* Heading & Subtitle */}
                  <div className="space-y-0.5">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-white font-headline tracking-tight flex items-center gap-2 flex-wrap">
                      <span>Queueing Your</span>
                      <span className="bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] bg-clip-text text-transparent">
                        Video...
                      </span>
                    </h2>
                    <p className="text-[11px] sm:text-xs text-[#9CA3AF] font-body">
                      Connecting to publishing pipeline and preparing &ldquo;{title || 'Your Story'}&rdquo;...
                    </p>
                  </div>

                  {/* Circular Progress Compact Card */}
                  <div className="w-full rounded-2xl border border-white/[0.08] bg-[#0A0D12] p-4 sm:p-4.5 shadow-2xl flex flex-col items-center justify-center text-center relative overflow-hidden">
                    {/* SVG Circular Glowing Progress Ring */}
                    <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center my-0.5">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        {/* Background Track */}
                        <circle
                          cx="50"
                          cy="50"
                          r="39"
                          stroke="rgba(255, 255, 255, 0.04)"
                          strokeWidth="5"
                          fill="none"
                        />
                        {/* Glowing Progress Arc */}
                        <defs>
                          <linearGradient id="queueingLoaderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#D0BCFF" />
                            <stop offset="100%" stopColor="#62FAE3" />
                          </linearGradient>
                          <filter id="queueingLoaderGlow" x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="2.5" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                          </filter>
                        </defs>
                        <circle
                          cx="50"
                          cy="50"
                          r="39"
                          stroke="url(#queueingLoaderGrad)"
                          strokeWidth="5.5"
                          strokeDasharray="245"
                          strokeDashoffset={245 - (245 * (Math.max(jobProgress || 45, 15) / 100))}
                          strokeLinecap="round"
                          fill="none"
                          filter="url(#queueingLoaderGlow)"
                          className="transition-all duration-700 ease-out"
                        />
                      </svg>

                      {/* Centered Percentage Inside Ring */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl sm:text-3xl font-extrabold font-headline bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] bg-clip-text text-transparent tracking-tight">
                          {jobProgress || 45}%
                        </span>
                      </div>
                    </div>

                    {/* Below Ring Status Text */}
                    <div className="mt-2 space-y-0.5">
                      <div className="text-[10px] sm:text-[11px] font-mono font-bold tracking-[0.18em] text-white/90 uppercase flex items-center justify-center gap-1.5">
                        <Loader2 className="w-3 h-3 text-[#62FAE3] animate-spin" />
                        <span>PROCESSING PIPELINE</span>
                      </div>
                      <div className="text-[9px] sm:text-[10px] font-mono tracking-[0.12em] text-[#9CA3AF]">
                        ALLOCATING GPU & AUDIO SYNTHESIZER...
                      </div>
                    </div>
                  </div>

                  {/* Publishing Destinations Card */}
                  <div className="w-full rounded-2xl border border-white/[0.08] bg-[#0A0D12] p-4 sm:p-4.5 shadow-2xl space-y-2.5 opacity-80">
                    <span className="text-[10px] font-mono font-bold tracking-widest text-[#9CA3AF] uppercase block">
                      PUBLISHING DESTINATIONS
                    </span>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3.5 h-3.5 rounded-[3px] border border-[#22D3EE] text-[#22D3EE] flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-white font-body">YouTube</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <div className="w-3.5 h-3.5 rounded-[3px] border border-[#22D3EE] text-[#22D3EE] flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-white font-body">YouTube Shorts</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <div className="w-3.5 h-3.5 rounded-[3px] border border-[#22D3EE] text-[#22D3EE] flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-white font-body">Instagram Reels</span>
                      </div>
                    </div>

                    <div className="border-t border-white/[0.08] pt-2 flex items-center justify-between text-[11px] font-mono text-[#9CA3AF]">
                      <span>Publishing Status —</span>
                      <span className="font-semibold text-[#62FAE3] flex items-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Initializing...</span>
                      </span>
                    </div>
                  </div>
                </div>
              ) : queueStatus === 'error' ? (
                /* ── Error / Failed State ── */
                <div className="w-full rounded-3xl border border-red-500/30 bg-[#140a0c]/90 backdrop-blur-2xl p-8 sm:p-10 shadow-[0_0_50px_rgba(239,68,68,0.12)] text-center relative overflow-hidden space-y-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center mx-auto shadow-[0_0_25px_rgba(239,68,68,0.25)]">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>

                  <div className="space-y-2">
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-headline tracking-tight">
                      Queueing <span className="text-red-400">Failed</span>
                    </h2>
                    <p className="text-xs sm:text-sm text-[#9CA3AF] font-body max-w-md mx-auto">
                      We encountered an issue adding your video to the publishing queue.
                    </p>
                  </div>

                  {queueError && (
                    <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-left max-w-md mx-auto">
                      <p className="text-xs font-mono text-red-300 break-words leading-relaxed">
                        {queueError}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 max-w-md mx-auto pt-2">
                    <button
                      type="button"
                      onClick={handleQueueVideo}
                      className="py-3 px-5 rounded-2xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs sm:text-sm font-headline tracking-wide hover:brightness-105 active:scale-98 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>Try Again</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => goToStep(5)}
                      className="py-3 px-5 rounded-2xl bg-white/[0.06] hover:bg-white/10 border border-white/15 text-white font-semibold text-xs sm:text-sm font-headline active:scale-98 transition-all cursor-pointer flex items-center justify-center"
                    >
                      <span>Back to Step 5</span>
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Exact Figma Screen: Video Queued Process (Success State) ── */
                <div className="w-full space-y-3.5 text-left animate-in fade-in duration-300">
                  {/* Heading & Subtitle */}
                  <div className="space-y-0.5">
                    <h2 className="text-xl sm:text-2xl font-extrabold text-white font-headline tracking-tight flex items-center gap-2 flex-wrap">
                      <span>Your Video</span>
                      <span className="text-[#38BDF8]">
                        Is Queued!
                      </span>
                      <span>🥳</span>
                    </h2>
                    <p className="text-[11px] sm:text-xs text-[#9CA3AF] font-body">
                      We&apos;re working on it. This page will update automatically.
                    </p>
                  </div>

                  {/* Circular Progress Compact Card */}
                  <div className="w-full rounded-2xl border border-white/[0.08] bg-[#0A0D12] p-4 sm:p-4.5 shadow-2xl flex flex-col items-center justify-center text-center relative overflow-hidden">
                    {/* SVG Circular Glowing Progress Ring */}
                    <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center my-0.5">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        {/* Background Track */}
                        <circle
                          cx="50"
                          cy="50"
                          r="39"
                          stroke="rgba(255, 255, 255, 0.04)"
                          strokeWidth="5"
                          fill="none"
                        />
                        {/* Glowing Progress Arc */}
                        <defs>
                          <linearGradient id="queuedFigmaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#D0BCFF" />
                            <stop offset="100%" stopColor="#62FAE3" />
                          </linearGradient>
                          <filter id="queuedFigmaGlow" x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="2.5" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                          </filter>
                        </defs>
                        <circle
                          cx="50"
                          cy="50"
                          r="39"
                          stroke="url(#queuedFigmaGrad)"
                          strokeWidth="5.5"
                          strokeDasharray="245"
                          strokeDashoffset={245 - (245 * ((typeof jobProgress === 'number' && jobProgress > 0 ? jobProgress : 68) / 100))}
                          strokeLinecap="round"
                          fill="none"
                          filter="url(#queuedFigmaGlow)"
                          className="transition-all duration-150 ease-out"
                        />
                      </svg>

                      {/* Centered Percentage Inside Ring */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl sm:text-3xl font-extrabold font-headline bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] bg-clip-text text-transparent tracking-tight">
                          {typeof jobProgress === 'number' && jobProgress > 0 ? jobProgress : 68}%
                        </span>
                      </div>
                    </div>

                    {/* Below Ring Status Text */}
                    <div className="mt-2 space-y-0.5">
                      <div className="text-[10px] sm:text-[11px] font-mono font-bold tracking-[0.18em] text-white/90 uppercase">
                        GENERATING VIDEO
                      </div>
                      <div className="text-[9px] sm:text-[10px] font-mono tracking-[0.12em] text-[#9CA3AF]">
                        ESTIMATED TIME REMAINING: ~2 MIN
                      </div>
                    </div>
                  </div>

                  {/* Publishing Destinations Card (Editor card omitted as requested) */}
                  <div className="w-full rounded-2xl border border-white/[0.08] bg-[#0A0D12] p-4 sm:p-4.5 shadow-2xl space-y-2.5">
                    <span className="text-[10px] font-mono font-bold tracking-widest text-[#9CA3AF] uppercase block">
                      PUBLISHING DESTINATIONS
                    </span>

                    <div className="space-y-2">
                      {/* YouTube Checkbox */}
                      <div className="flex items-center gap-2.5">
                        <div className="w-3.5 h-3.5 rounded-[3px] border border-[#22D3EE] text-[#22D3EE] flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-white font-body">YouTube</span>
                      </div>

                      {/* YouTube Shorts Checkbox */}
                      <div className="flex items-center gap-2.5">
                        <div className="w-3.5 h-3.5 rounded-[3px] border border-[#22D3EE] text-[#22D3EE] flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-white font-body">YouTube Shorts</span>
                      </div>

                      {/* Instagram Reels Checkbox */}
                      <div className="flex items-center gap-2.5">
                        <div className="w-3.5 h-3.5 rounded-[3px] border border-[#22D3EE] text-[#22D3EE] flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-white font-body">Instagram Reels</span>
                      </div>
                    </div>

                    <div className="border-t border-white/[0.08] pt-2 flex items-center justify-between text-[11px] font-mono text-[#9CA3AF]">
                      <span>Publishing Status —</span>
                      <span className="font-semibold text-[#C084FC]">Queued</span>
                    </div>
                  </div>

                  {/* Action Buttons Row */}
                  <div className="flex items-center gap-3 pt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => navigate('/tasks')}
                      style={{ background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }}
                      className="py-2.5 sm:py-3 px-6 rounded-xl text-[#06080D] font-bold text-xs font-mono tracking-wider uppercase hover:opacity-95 active:scale-98 transition-all shadow-[0_0_20px_rgba(98,250,227,0.35)] cursor-pointer"
                    >
                      VIEW PUBLISHING QUEUE
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="py-2.5 sm:py-3 px-6 rounded-xl bg-[#0A0D12] hover:bg-white/[0.06] border border-white/20 text-white font-bold text-xs font-mono tracking-wider uppercase active:scale-98 transition-all cursor-pointer"
                    >
                      BACK TO DASHBOARD
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ─────────────────────────────────────────
              FINAL VIEW: Final Video Deck (Figma FINAL.png Exact)
          ───────────────────────────────────────── */}
          {currentStep === 'final' && (
            <motion.div
              key="final-screen"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto w-full space-y-3.5 my-auto text-left"
            >
              {!(projectId || queueStatus === 'success') ? (
                /* ── Direct Access / Idle State (When no video is generated yet) ── */
                <div className="w-full rounded-3xl border border-white/10 bg-[#091114]/90 backdrop-blur-2xl p-8 sm:p-12 shadow-2xl text-center relative overflow-hidden space-y-6">
                  <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto shadow-inner text-[#2DD4BF]">
                    <Film className="w-8 h-8 opacity-80" />
                  </div>

                  <div className="space-y-2 max-w-md mx-auto">
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-headline tracking-tight">
                      Final Video <span className="text-[#62FAE3]">Deck</span>
                    </h2>
                    <p className="text-xs sm:text-sm text-[#9CA3AF] font-body leading-relaxed">
                      No video has been generated yet. Complete your story setup or click preview below to view the finalized 4K video deck.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg mx-auto pt-2">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="py-3 px-4 rounded-2xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs font-headline tracking-wide hover:brightness-105 active:scale-98 transition-all shadow-[0_0_20px_rgba(94,234,212,0.25)] cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <span>Start Story</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTitle('A DOG STORY');
                        setProjectId('demo-dog-story');
                        setQueueStatus('success');
                      }}
                      className="py-3 px-4 rounded-2xl bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/30 text-[#62FAE3] font-semibold text-xs font-headline active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <TwinneeLogo className="w-3.5 h-3.5" />
                      <span>Preview Deck</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="py-3 px-4 rounded-2xl bg-white/[0.06] hover:bg-white/10 border border-white/15 text-white font-semibold text-xs font-headline active:scale-98 transition-all cursor-pointer flex items-center justify-center"
                    >
                      <span>Dashboard</span>
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Completed Video Output Deck (Figma FINAL.png Exact) ── */
                <>
                  {/* Top Breadcrumb */}
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-1.5 text-[11px] font-mono tracking-widest text-[#9CA3AF] hover:text-white uppercase transition-colors mb-0.5 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>DASHBOARD / YOUR VIDEO</span>
                  </button>

                  {/* Title */}
                  <div>
                    <h1 className="text-3xl sm:text-4xl lg:text-[40px] font-black tracking-tight uppercase font-headline text-white leading-none">
                      {title && title.toUpperCase() !== 'A DOG STORY' ? (
                        <span>{title}</span>
                      ) : (
                        <>
                          <span>A DOG </span>
                          <span className="text-[#93C5FD]">STORY</span>
                    </>
                  )}
                </h1>
              </div>

                  {/* Video Player Card Frame (Figma Exact) */}
                  <div className="w-full rounded-2xl sm:rounded-3xl border border-white/[0.08] bg-[#0A0D12] overflow-hidden shadow-2xl relative group">
                    <div className="aspect-video w-full relative overflow-hidden flex items-center justify-center bg-black/70">
                      <img
                        src={displayThumbnail || '/flowImages/1.png'}
                        alt={title || 'Final Video Render'}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 pointer-events-none" />

                      {/* Center Dark Glass Circular Play Button */}
                  <button
                    type="button"
                    onClick={() => setFinalVideoPlaying(!finalVideoPlaying)}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-black/50 backdrop-blur-md border border-white/20 text-white flex items-center justify-center shadow-2xl hover:scale-110 active:scale-95 transition-all cursor-pointer z-10"
                        title={finalVideoPlaying ? 'Pause Video' : 'Play Video'}
                  >
                    {finalVideoPlaying ? (
                          <Pause className="w-6 h-6 fill-current" />
                    ) : (
                          <Play className="w-6 h-6 fill-current translate-x-0.5" />
                    )}
                  </button>
                </div>
              </div>

                  {/* 3 Action Cards (Figma FINAL.png Exact) */}
                  <div className="w-full space-y-2.5 pt-1">
                    {/* 1. Download Video */}
                <div
                  onClick={() => {
                    toast.success('Initiating 4K video export download...');
                  }}
                      className="w-full rounded-2xl border border-white/[0.08] bg-[#0A0D12] hover:bg-[#121820] hover:border-white/20 p-4 sm:p-4.5 transition-all flex items-center gap-4 cursor-pointer shadow-xl group"
                >
                      <div className="w-11 h-11 rounded-xl bg-white/[0.03] border border-white/10 text-[#62FAE3] flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:border-[#62FAE3]/40 transition-all">
                        <Download className="w-5 h-5" />
                  </div>
                      <div className="space-y-0.5 text-left">
                        <h3 className="text-sm sm:text-base font-bold text-white font-headline leading-tight">
                          Download Video
                        </h3>
                        <p className="text-xs text-[#9CA3AF] font-body">
                          Save your creation in high-quality 4K
                        </p>
                  </div>
                </div>

                    {/* 2. Open in editor */}
                <div
                  onClick={() => {
                        if (projectId && projectId !== 'demo-dog-story') {
                      navigate(`/narration/${projectId}/scenes`);
                    } else {
                          toast.info('Opening editor timeline...');
                    }
                  }}
                      className="w-full rounded-2xl border border-white/[0.08] bg-[#0A0D12] hover:bg-[#121820] hover:border-white/20 p-4 sm:p-4.5 transition-all flex items-center gap-4 cursor-pointer shadow-xl group"
                >
                      <div className="w-11 h-11 rounded-xl bg-white/[0.03] border border-white/10 text-[#62FAE3] flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:border-[#62FAE3]/40 transition-all">
                        <Edit3 className="w-5 h-5" />
                  </div>
                      <div className="space-y-0.5 text-left">
                        <h3 className="text-sm sm:text-base font-bold text-white font-headline leading-tight">
                          Open in editor
                        </h3>
                        <p className="text-xs text-[#9CA3AF] font-body">
                          Fine-tune timeline, audio, and scenes
                        </p>
                  </div>
                </div>

                    {/* 3. View in Tasks */}
                <div
                      onClick={() => navigate('/tasks')}
                      className="w-full rounded-2xl border border-white/[0.08] bg-[#0A0D12] hover:bg-[#121820] hover:border-white/20 p-4 sm:p-4.5 transition-all flex items-center gap-4 cursor-pointer shadow-xl group"
                >
                      <div className="w-11 h-11 rounded-xl bg-white/[0.03] border border-white/10 text-[#62FAE3] flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:border-[#62FAE3]/40 transition-all">
                        <Clapperboard className="w-5 h-5" />
                  </div>
                      <div className="space-y-0.5 text-left">
                        <h3 className="text-sm sm:text-base font-bold text-white font-headline leading-tight">
                          View in Tasks
                        </h3>
                        <p className="text-xs text-[#9CA3AF] font-body">
                          Full job history and status
                        </p>
                  </div>
                </div>
              </div>

                  {/* Restart Bottom Row */}
                  <div className="pt-2 flex items-center justify-between border-t border-white/5">
                <button
                  onClick={() => {
                    setCurrentStep(1);
                    setTitle('');
                    setShortIdea('');
                        setQueueStatus('idle');
                        setProjectId(null);
                        setJobId(null);
                  }}
                  className="flex items-center gap-1.5 text-xs font-mono text-[#9CA3AF] hover:text-white transition-colors cursor-pointer"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Create another story</span>
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-mono font-semibold transition-colors cursor-pointer"
                >
                  Back to Dashboard
                </button>
              </div>
                </>
              )}
            </motion.div>
          )}
        </main>

        {/* ─────────────────────────────────────────
            RIGHT ROADMAP & QUEUE SUMMARY SIDEBAR (Figma Exact Process Card)
        ───────────────────────────────────────── */}
        {currentStep !== 'final' && (
          <aside className="w-full lg:w-72 xl:w-80 shrink-0 self-center my-auto px-1 sm:px-2 space-y-3.5">
            {/* ── 1. Main PROCESS Card (Spacious Card Layout) ── */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#0A0D12] p-6 sm:p-7 shadow-2xl space-y-6 text-left animate-in fade-in duration-200 min-h-[490px] lg:min-h-[540px] xl:min-h-[580px] flex flex-col justify-start">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b border-white/[0.08]">
                <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-[#9CA3AF] block">
                  PROCESS
              </span>
                <span className="text-xs font-mono text-[#62FAE3] font-bold">
                  {progressPercent}%
              </span>
            </div>

              {/* Process Stepper List with spacious vertical gaps */}
              <div className="space-y-5 sm:space-y-6 py-2 flex-1 flex flex-col justify-start">
                {ROADMAP_STEPS.map((step) => {
                const isCurrent = currentStep === step.id;
                  const isComplete =
                  typeof currentStep === 'number'
                      ? currentStep > step.id
                    : currentStep === 'final';

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setCurrentStep(step.id)}
                      className={`w-full text-left flex items-start gap-4 sm:gap-4.5 transition-all cursor-pointer group py-1.5 ${!isCurrent && !isComplete ? 'opacity-55 hover:opacity-85' : ''
                        }`}
                    >
                      {/* Step Indicator Dot / Checkmark */}
                      {isComplete ? (
                        <div className="w-5 h-5 rounded-full border border-[#22D3EE] text-[#22D3EE] flex items-center justify-center shrink-0 mt-0.5 shadow-[0_0_8px_rgba(34,211,238,0.3)] bg-[#22D3EE]/10">
                          <Check className="w-2.5 h-2.5 stroke-[2.5]" />
                        </div>
                      ) : isCurrent ? (
                        <div className="w-5 h-5 rounded-full border border-[#22D3EE] flex items-center justify-center shrink-0 mt-0.5 shadow-[0_0_8px_rgba(34,211,238,0.4)] bg-[#22D3EE]/10">
                          <div className="w-2 h-2 rounded-full bg-[#22D3EE] shadow-[0_0_6px_#22D3EE] animate-pulse" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center shrink-0 mt-0.5 group-hover:border-white/40 transition-colors" />
                      )}

                      {/* Step Title + Status directly underneath (Figma Exact) */}
                      <div className="space-y-1 min-w-0 flex-1">
                        <div
                          className={`text-xs sm:text-[13px] font-headline leading-tight transition-colors ${isCurrent
                            ? 'font-bold text-[#22D3EE]'
                            : isComplete
                              ? 'font-semibold text-white group-hover:text-[#62FAE3]'
                              : 'font-medium text-gray-400 group-hover:text-gray-300'
                        }`}
                    >
                      {step.title}
                        </div>
                        {isComplete ? (
                          <div className="text-[10px] sm:text-[11px] text-[#9CA3AF] font-body leading-tight">
                            Complete
                          </div>
                        ) : isCurrent ? (
                          <div className="text-[10px] sm:text-[11px] text-[#22D3EE] font-mono leading-tight">
                            {currentStep === 6
                              ? `In Progress (${typeof jobProgress === 'number' && jobProgress > 0 ? jobProgress : 68}%)`
                              : 'In Progress'}
                          </div>
                    ) : null}
                      </div>
                  </button>
                );
              })}
            </div>
          </div>

            {/* ── 2. Separate Small Bottom Info Card (Figma Exact) ── */}
            <button
              type="button"
              onClick={() => navigate('/tasks')}
              className="w-full py-3.5 rounded-2xl border border-white/[0.08] bg-[#0A0D12] hover:bg-white/[0.04] flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer shadow-lg group"
              title="View Task Details"
            >
              <Info className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
            </button>
        </aside>
        )}
      </div>

      {/* Footer (Compact) */}
      <footer className="relative z-10 w-full py-2 text-center text-[11px] text-[#6B7280] font-mono border-t border-white/5 shrink-0">
        Twin Tales AI Studio • Scene-Native Video Generation
      </footer>

      {/* ─────────────────────────────────────────
          VOICE LIBRARY POPUP MODAL
      ───────────────────────────────────────── */}
      {isVoicesModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="relative w-full max-w-3xl max-h-[88vh] bg-[#0a0d14] border border-white/15 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between bg-white/[0.02]">
              <div>
                <h2 className="text-lg font-bold text-white font-headline flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-[#2DD4BF]" />
                  <span>Studio Voice Library</span>
                </h2>
                <p className="text-xs text-[#9CA3AF] font-body">Browse, audition, and select from our studio voices</p>
              </div>

              <button
                type="button"
                onClick={() => setIsVoicesModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-[#9CA3AF] hover:text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Filters Bar */}
            <div className="p-4 border-b border-white/10 space-y-3 bg-[#06080d]/60">
              <div className="flex flex-wrap items-center justify-between gap-2.5">
                {/* Provider Selector */}
                <div className="flex items-center gap-1 p-1 bg-white/[0.04] border border-white/10 rounded-xl">
                  <button
                    onClick={() => setModalProviderFilter('all')}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold font-body transition-all cursor-pointer ${modalProviderFilter === 'all'
                      ? 'bg-[#2DD4BF]/20 border border-[#2DD4BF]/60 text-[#2DD4BF]'
                      : 'text-[#9CA3AF] hover:text-white'
                      }`}
                  >
                    All Providers
                  </button>
                  {VOICE_PROVIDERS.map((provider) => (
                    <button
                      key={provider.id}
                      onClick={() => setModalProviderFilter(provider.id)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold font-body transition-all cursor-pointer ${modalProviderFilter === provider.id
                        ? 'bg-[#2DD4BF]/20 border border-[#2DD4BF]/60 text-[#2DD4BF]'
                        : 'text-[#9CA3AF] hover:text-white'
                        }`}
                    >
                      {provider.name}
                    </button>
                  ))}
                </div>

                {/* Language Filter */}
                <div className="flex items-center gap-1 p-1 bg-white/[0.04] border border-white/10 rounded-xl">
                  {[
                    { id: 'all', label: 'All Lang' },
                    { id: 'English', label: 'English' },
                    { id: 'Hindi', label: 'Hindi' },
                  ].map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => setModalLanguageFilter(lang.id)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold font-body transition-all cursor-pointer ${modalLanguageFilter === lang.id
                        ? 'bg-white/15 text-white border border-white/20'
                        : 'text-[#9CA3AF] hover:text-white'
                        }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>

                {/* Gender Filter */}
                <div className="flex items-center gap-1 p-1 bg-white/[0.04] border border-white/10 rounded-xl">
                  {['all', 'male', 'female'].map((g) => (
                    <button
                      key={g}
                      onClick={() => setModalGenderFilter(g)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold font-body uppercase transition-all cursor-pointer ${modalGenderFilter === g
                        ? 'bg-white/15 text-white border border-white/20'
                        : 'text-[#9CA3AF] hover:text-white'
                        }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 2: Search Input */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={modalSearchQuery}
                    onChange={(e) => setModalSearchQuery(e.target.value)}
                    placeholder="Search voices by name, style, or language..."
                    className="w-full h-10 rounded-xl bg-white/[0.04] border border-white/10 pl-9 pr-3 text-white text-xs placeholder:text-[#4B5563] focus:border-[#2DD4BF]/60 focus:outline-none transition-colors font-body"
                  />
                </div>
              </div>
            </div>

            {/* Voices List Grid */}
            <div className="p-4 overflow-y-auto flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 scrollbar-none">
              {allAvailableVoices
                .filter((v) => modalProviderFilter === 'all' || v.provider === modalProviderFilter)
                .filter((v) => modalLanguageFilter === 'all' || v.language?.toLowerCase().includes(modalLanguageFilter.toLowerCase()))
                .filter((v) => modalGenderFilter === 'all' || v.gender === modalGenderFilter)
                .filter(
                  (v) =>
                    !modalSearchQuery ||
                    v.name.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
                    v.tagline.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
                    v.language.toLowerCase().includes(modalSearchQuery.toLowerCase())
                )
                .map((voice) => {
                  const isSelected = selectedVoiceId === voice.id;
                  const isCardPlaying = isPlayingVoice && playingVoiceCardId === voice.id;

                  return (
                    <div
                      key={voice.id}
                      onClick={() => {
                        setSelectedVoiceId(voice.id);
                        setSelectedVoiceProvider(voice.provider);
                        toast.success(`Voice selected: ${voice.name}`);
                        setIsVoicesModalOpen(false);
                      }}
                      className={`p-3 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${isSelected
                        ? 'border-[#2DD4BF] bg-[#0c141d] shadow-[0_0_15px_rgba(45,212,191,0.2)] ring-1 ring-[#2DD4BF]/60'
                        : 'border-[#1E2533] bg-[#0a0d14] hover:border-white/20 hover:bg-[#0f141f]'
                        }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-11 h-11 rounded-full overflow-hidden p-0.5 border shrink-0 ${isSelected ? 'border-[#2DD4BF]' : 'border-white/20'
                            }`}
                        >
                          <img
                            src={voice.avatar}
                            alt={voice.name}
                            className="w-full h-full object-cover rounded-full"
                          />
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h4 className="text-xs font-bold text-white font-headline truncate">{voice.name}</h4>
                            <span className="px-1.5 py-0.2 rounded text-[8px] font-mono font-bold uppercase bg-white/10 text-gray-300 border border-white/15">
                              {voice.gender}
                            </span>
                          </div>
                          <p className="text-[10px] text-[#9CA3AF] font-body truncate">{voice.tagline}</p>
                          <span className="text-[9px] font-mono text-[#2DD4BF] opacity-80 uppercase">
                            {voice.provider}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePlayVoice(voice);
                          }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer ${isCardPlaying
                            ? 'bg-[#2DD4BF] text-[#06080d] shadow-[0_0_10px_#2DD4BF]'
                            : 'bg-white/10 hover:bg-white/20 text-white'
                            }`}
                        >
                          {isCardPlaying ? (
                            <Pause className="w-3.5 h-3.5 fill-current" />
                          ) : (
                            <Play className="w-3.5 h-3.5 fill-current translate-x-0.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NarrationFlowPage;
